import { aktuelleSprache } from './sprache'
import type { Sprachschluessel } from './sprache'
import { meldeStoerung, rufe } from './Stoerungsstelle'
import { istVeraltet, KOPF, vergleiche } from './Fassung'
import { meldeGespeichert } from './Nachladen'
import type { Erfolgsmomentfrage, Seriestand, Spielschalterangabe } from './Spiel'
import type { Zahlendarstellungangabe } from './Zahlenwahl'
import type { Ueberwachungsfestlegung, Ueberwachungsstand } from './Uebersichtsblatt'
import type {
  Packagevorlagenauftrag,
  Packagevorlagengrundlagen,
  Packagevorlagenvorschau,
} from './Packagezuschnitt'
import type {
  Umrechnung,
  Austauschergebnis,
  Prozessgrundlage,
  Austauschvorschau,
  Abschluss,
  Anwenderangabe,
  Anwenderauftrag,
  Angemeldet,
  Rolle,
  Angabenkatalogeintrag,
  Anhangsbuch,
  Handbuchausgabe,
  Regelparameter,
  Abschlusszeile,
  Rechenstand,
  Prozessnachweis,
  Schrittbild,
  Schrittzeile,
  Auswahl,
  Bericht,
  Buchungszeile,
  Freigabeurteil,
  Freigabeprotokollzeile,
  Pruefregelzeile,
  Pruefstufe,
  Messwerte,
  Nachweispruefung,
  Regelnachweis,
  Objektartbeschreibung,
  Datenquelle,
  Importlaufkopf,
  Datenimportstand,
  Datenimportloeschvorschau,
  Dateiprobe,
  Zielfeld,
  Importvorschau,
  Quellperiode,
  Packagebericht,
  Packagevorschau,
  Stapelbericht,
  Stapelvorschau,
  Aktualisierungsvorschau,

  Gliederungsbild,
  Kontoposition,
  Kontenmapping,
  Bewegungsartmapping,
  Bewegungsartanlage,
  Kernbewegungsart,
  Wertzuordnung,
  Wertanlage,
  VerwendeteZuordnungen,
  Verlaufseintrag,
  Handgriff,
  Kontoanlage,
  Anlagevorschau,
  Kontoeinfuegung,
  Kfreinfuegung,
  Statistikkontoeinfuegung,
  Kfrloeschlage,
  Reihenfolge,
  Pflegeauftrag,
  Verwendung,
  Aussonderung,
  Loeschlage,
  Loeschauftrag,
  Pflegeergebnis,
  Spiegel,
  Kapitalflussrechnung,
  Kfrnachweis,
  Kfrperiodenbericht,
  Vorschlagsrichtung,
  Gliederungszug,
  Aufbaubild,
  Stammdatenblatt,
  Haussprache,
  Stammdatenliste,
  Periodenbericht,
  Rechenantwort,
  Periodenschluss,
  Positionsplan,
  Stammdatenkatalog,
  Textbaustein,
  Belegkopf,
  Belegzeile,
  Belegordner,
  Ordnerbeleg,
  Buchungsrahmen,
  Erfassungsauftrag,
  Journaltreffer,
  Loeschvorschau,
  Protokolleintrag,
  Vorgangskopf,
  Wertenachweis as WertenachweisSatz,
  Achsenangabe,
  Zusatzaufriss,
  Periodenzusatzaufriss,
  Spiegelzusatzaufriss,
  Kfrzusatzaufriss,
  Kfrperiodenzusatzaufriss,
  Waehrungspaartafel,
  Kurstafel,
  Kursart,
  Kursvorschau,
  Kursuebernahme,
  Positionskurs,
  Eintrittskursvorschlag,
  HistorischerKurs,
  HistorischeKursluecke,
  HistorischerKurseintrag,
  HistorischeKurszeile,
  Zeitachsenstand,
  Icabstimmblatt,
  Korrekturangabe,
  Anzeigewaehrungen,
  Berichtswelt,
  Stichtagskursanpassungsstand,
} from './typen'

/**
 * Der Filter der Zusatzachsen als Anfrageparameter (7.3 Abschnitt 2f):
 * `partner=EH-PROD` filtert, `partner=` (leer) heißt „Default" (R429),
 * ein fehlender Parameter lässt die Achse frei. Dieselbe Schreibweise für
 * jeden Endpunkt, der einen Pfad entgegennimmt.
 */
function mitWeiteren(teil: URLSearchParams, weitere?: Record<string, string | null>): URLSearchParams {
  for (const [achse, wert] of Object.entries(weitere ?? {})) {
    teil.set(achse, wert ?? '')
  }

  return teil
}

/**
 * Ein Rechenabbruch aus dem Kern — kein Programmfehler, sondern ein Befund.
 *
 * Er trägt die Handreichung mit: den Satz, der sagt, welcher Handgriff fehlt.
 * Genau das unterscheidet eine Meldung, mit der ein Fachanwender weiterkommt,
 * von einer, für die er die IT anrufen muss.
 *
 * Beides kommt als **Schlüssel mit Werten**, nicht als Satz — der Kern
 * formuliert nicht mehr. So wechselt auch ein stehender Abbruch die Sprache
 * mit, ohne dass jemand neu rechnen müsste.
 */
export class Rechenabbruch extends Error {
  readonly meldung: Textbaustein
  readonly handreichung: Textbaustein | null

  constructor(meldung: Textbaustein, handreichung: Textbaustein | null) {
    super(meldung.schluessel)
    this.name = 'Rechenabbruch'
    this.meldung = meldung
    this.handreichung = handreichung
  }
}

/**
 * Eine Störung der Anlage — kein Befund und kein Bedienfehler.
 *
 * Auch sie nennt drei Dinge (R145), aber in Betriebssprache und an den
 * Administrator gerichtet: Der Fachanwender kann eine unerreichbare Datenbank
 * nicht beheben, und ein Statuscode hilft ihm dabei nicht. Wer einen
 * Fehlercode liest, ruft in der Zentrale an — genau diesen Anruf soll L6
 * abschaffen.
 *
 * Sie trägt einen **Schlüssel, keinen Satz.** Der Satz entsteht erst beim
 * Anzeigen: Wer die Sprache wechselt, während eine Meldung steht, soll sie
 * nicht in der alten Sprache weiterlesen.
 */
export class Betriebsstoerung extends Error {
  readonly schluessel: Sprachschluessel

  /**
   * Was der Dienst selbst über die Störung weiß — etwa daß die EZB nicht
   * geantwortet hat, und welcher Weg bleibt (7.2 Abschnitt 30, R1008).
   * `null`, wo nur die Oberfläche etwas weiß: Der Dienst war nicht zu
   * erreichen oder brach ab.
   */
  readonly meldung: Textbaustein | null

  readonly handreichung: Textbaustein | null

  /** Wann es geschah — für den Anruf beim Betrieb (R1005). */
  readonly zeit: Date

  /** Unter welcher Kennung der Dienst den Fehler protokolliert hat (R1005). */
  readonly kennung: string | null

  /**
   * **Ein Abruf im Hintergrund meldet nichts** (R1007). Wer ihn ausgelöst hat,
   * setzt das im eigenen `catch` — gemeldet wird erst danach.
   */
  still = false

  constructor(
    schluessel: Sprachschluessel,
    angaben: { meldung?: Textbaustein | null; handreichung?: Textbaustein | null; kennung?: string | null } = {},
  ) {
    super(schluessel)
    this.name = 'Betriebsstoerung'
    this.schluessel = schluessel
    this.meldung = angaben.meldung ?? null
    this.handreichung = angaben.handreichung ?? null
    this.zeit = new Date()
    this.kennung = angaben.kennung ?? null
  }
}

/**
 * **Jede Störung endet im Störungsfenster** (7.2 Abschnitt 30, R1003) — an
 * dieser einen Stelle für alle Bildschirme.
 *
 * Gemeldet wird einen Augenblick später: Erst laufen die `catch`-Zweige der
 * Aufrufer, und ein Abruf im Hintergrund kann sich dort als still erklären
 * (R1007). Ohne diesen Aufschub müsste jeder Abruf schon beim Abschicken
 * wissen, ob jemand auf ihn wartet.
 */
export function gemeldet(stoerung: Betriebsstoerung): Betriebsstoerung {
  window.setTimeout(() => {
    if (!stoerung.still) {
      meldeStoerung(stoerung)
    }
  }, 0)
  return stoerung
}

/** Eine Störung als still erklären (R1007) — anderes bleibt, wie es ist. */
export function still(fehler: unknown): unknown {
  if (fehler instanceof Betriebsstoerung) {
    fehler.still = true
  }
  return fehler
}

/**
 * **Ein Abruf im Hintergrund** (7.2 Abschnitt 30, R1007): Er meldet keine
 * Störung, versucht es aber nach wenigen Sekunden einmal erneut — ein kurzer
 * Aussetzer heilt so von selbst. Die Auflage zur Entscheidung, solche Abrufe
 * still zu halten.
 */
export async function imHintergrund<T>(abruf: () => Promise<T>, pause = 3000): Promise<T> {
  try {
    return await abruf()
  } catch (erster) {
    still(erster)
    if (!(erster instanceof Betriebsstoerung)) {
      throw erster
    }
  }

  await new Promise((weiter) => window.setTimeout(weiter, pause))
  try {
    return await abruf()
  } catch (zweiter) {
    throw still(zweiter)
  }
}

/**
 * Die Störung zu einer Antwort, die weder gelingt noch ein Befund ist.
 *
 * **Die Kennung kommt aus dem Rumpf**: Bricht der Dienst ab, schreibt er den
 * Fehler unter einer Kennung ins Protokoll und schickt sie mit (R1005).
 */
export async function stoerungAus(antwort: Response): Promise<Betriebsstoerung> {
  const koerper = await antwort
    .clone()
    .json()
    .catch(() => null)
  const kennung = typeof koerper?.traceId === 'string' ? (koerper.traceId as string) : null

  return gemeldet(
    // **Bricht der Server ab, heißt es „abgebrochen"** (R1052): Er hat
    // geantwortet, nur eben mit einem Abbruch. Bis zum 2026-10-04 stand hier
    // „nicht beantwortet", und das liest sich wie ein Netzproblem.
    new Betriebsstoerung(antwort.status >= 500 ? 'meldung.stoerung.abgebrochen' : 'meldung.stoerung.abgewiesen', {
      kennung,
    }),
  )
}

/**
 * Eine Antwort, die weder Datei noch Befund ist — mit ihrer Nummer, damit der
 * Bildschirm sagen kann, was sie bedeutet (R145).
 */
export class Antwortfehler extends Error {
  readonly status: number

  constructor(status: number) {
    super(`Antwort ${status}`)
    this.name = 'Antwortfehler'
    this.status = status
  }
}

/* --- Die Sitzung (8.1, R355) ----------------------------------------------- */

/**
 * Die Sitzung ist fort — abgemeldet, abgelaufen oder gesperrt.
 *
 * **Sie wird nicht als Störung behandelt** (R145): Eine abgelaufene Sitzung ist
 * kein Fehler, sondern ein Zustand, und die Antwort darauf ist die Anmeldung.
 * Deshalb meldet sie sich als Ereignis an das ganze Fenster — jeder Ruf kann
 * sie auslösen, und nur eine Stelle muss darauf antworten.
 */
export const ABGEMELDET = 'coko.abgemeldet'

export class Nichtangemeldet extends Error {
  constructor() {
    super('nicht angemeldet')
    this.name = 'Nichtangemeldet'
  }
}

/**
 * Ein fachlicher Befund mit einem Schlüssel — „das Kennwort ist zu kurz",
 * „der Name ist vergeben", „das ist der letzte Verwalter".
 */
export class Fachbefund extends Error {
  readonly schluessel: Sprachschluessel

  constructor(schluessel: Sprachschluessel) {
    super(schluessel)
    this.name = 'Fachbefund'
    this.schluessel = schluessel
  }
}

/** Der Kennwortwechsel steht vor allem anderen (R362). */
export class KennwortNoetig extends Error {
  constructor() {
    super('Kennwortwechsel nötig')
    this.name = 'KennwortNoetig'
  }
}

/*
  **Die Anzeigewährung reist mit jedem Bericht** (2.12 Abschnitt 3.21, R993).

  Gewählt wird sie einmal über dem Bericht, und sie gilt für alle Berichte, bis
  sie geändert wird. Deshalb steht sie hier und nicht an jedem Abruf: Der Dienst
  rechnet jeden Bericht um, der sie mitbringt — Bilanz, GuV, Periodenbild,
  Spiegel, Kapitalflussrechnung, Wertenachweis und ihre Aufrisse. Ein Abruf,
  der sie vergäße, zeigte eine Zahl in der Ausgangswährung unter einem Feld,
  das eine andere nennt.
*/
let anzeigewaehrung: string | null = null

/** `null`: Die Berichte stehen in ihrer Ausgangswährung. */
export function setzeAnzeigewaehrung(waehrung: string | null): void {
  anzeigewaehrung = waehrung
}

/** Die Wege, deren Antwort eine Zahl in Berichtswährung trägt. */
export const BERICHTSWEGE: ReadonlySet<string> = new Set([
  '/api/berichte/geliefert/bilanz',
  '/api/berichte/geliefert/guv',
  '/api/berichte/geliefert/perioden',
  '/api/berichte/geliefert/aufriss',
  '/api/berichte/geliefert/wertenachweis',
  '/api/berichte/perioden',
  '/api/berichte/perioden/aufriss',
  '/api/berichte/aufriss',
  '/api/berichte/wertenachweis',
  '/api/berichte/spiegel',
  '/api/berichte/spiegel/aufriss',
  '/api/berichte/kapitalfluss',
  '/api/berichte/kapitalfluss/perioden',
  '/api/berichte/kapitalfluss/perioden/aufriss',
  '/api/berichte/kapitalfluss/aufriss',
  '/api/berichte/kapitalfluss/nachweis',
])

/*
  **Nachweis und Aufriss stehen in der Währung ihres Berichts** (2.12 Abschnitt
  3.26, R1117). Ein Bericht über zwölf Monate braucht mehr Kurse als der
  Nachweis einer seiner Zellen; fehlt einer, bleibt der Bericht in seiner
  Ausgangswährung, und der Nachweis rechnete trotzdem um. Am 2026-10-05 stand so
  die Kapitalflussrechnung der Tochter USA in USD und der Nachweis derselben
  Zelle in EUR. Deshalb merkt sich diese Schicht, ob der Bericht, der gerade zu
  sehen ist, umgerechnet wurde — gesetzt beim Zeichnen, wie die Währung selbst.
*/
let berichtsumrechnung: Umrechnung | null = null

/** Die Umrechnung des Berichts, der gerade zu sehen ist — `null`, wo keiner steht. */
export function setzeBerichtsumrechnung(umrechnung: Umrechnung | null): void {
  berichtsumrechnung = umrechnung
}

/** Ein Weg, der in einen Bericht hineinführt: Aufriss und Nachweis. */
export function istAufrissweg(weg: string): boolean {
  return /\/(aufriss|nachweis|wertenachweis)$/.test(weg)
}

/**
 * Die Umrechnung des Berichts, wenn er in seiner Ausgangswährung geblieben ist
 * — dann sagt der Nachweis dasselbe wie er (R1117). Sonst `null`.
 */
export function berichtNichtUmgerechnet(): Umrechnung | null {
  return berichtsumrechnung && !berichtsumrechnung.umgerechnet ? berichtsumrechnung : null
}

export function mitAnzeigewaehrung(
  pfad: string,
  waehrung: string | null = berichtNichtUmgerechnet() && istAufrissweg(pfad.split('?')[0] ?? pfad)
    ? null
    : anzeigewaehrung,
): string {
  const weg = pfad.split('?')[0] ?? pfad
  if (!waehrung || !(BERICHTSWEGE.has(weg) || /^\/api\/laeufe\/\d+\/bericht\/(bilanz|guv)$/.test(weg))) {
    return pfad
  }

  return `${pfad}${pfad.includes('?') ? '&' : '?'}anzeigewaehrung=${encodeURIComponent(waehrung)}`
}

async function hole<T>(pfad: string, einstellungen?: RequestInit): Promise<T> {
  let antwort: Response

  // Bei Formulardaten setzt der Browser den Inhaltstyp selbst — mitsamt der
  // Grenzmarke, die den Dateiteil abtrennt. Wer hier „application/json"
  // daraufschreibt, schickt eine Datei, die niemand mehr zerlegen kann.
  const formular = einstellungen?.body instanceof FormData

  // **Die Sprache reist mit** (R588). Ein neu angelegtes Objekt bekommt genau
  // eine Beschriftungszeile, und zwar in der Sprache, in der der Anwender
  // gerade arbeitet — das weiß nur die Oberfläche: Am Anwender steht, womit er
  // das letzte Mal angefangen hat, nicht, was er oben rechts gewählt hat.
  //
  // **Nicht „Accept-Language"**: Das ist die Sprache des Browsers. Die
  // Anwendung hat ihre eigene, und sie kann eine andere sein.
  const koepfe: Record<string, string> = formular
    ? { 'Coko-Sprache': aktuelleSprache() }
    : { 'Content-Type': 'application/json', 'Coko-Sprache': aktuelleSprache() }

  const lesend = !einstellungen?.method || einstellungen.method === 'GET'

  // **Eine veraltete Seite speichert nicht** (7.2 Abschnitt 46, R1441): Sie
  // schickte Angaben, die der Server womöglich anders nennt.
  if (!lesend && istVeraltet()) {
    throw new Fachbefund('fassung.veraltet.speichern')
  }

  // **Wer wartet, sieht es** (R1005, R1250): Dauert ein Abruf, zeigt die
  // Anwendung es, statt stumm zu stehen — über die gemeinsame Abrufstelle.
  try {
    antwort = await rufe(lesend ? mitAnzeigewaehrung(pfad) : pfad, {
      ...einstellungen,
      // **Das Sitzungscookie ist HttpOnly** (R355) — die Oberfläche kommt
      // nicht daran und muss es auch nicht: Sie sagt nur, dass es mitreisen
      // soll.
      credentials: 'same-origin',
      headers: { ...koepfe, ...(einstellungen?.headers as Record<string, string> | undefined) },
    })
  } catch {
    throw gemeldet(new Betriebsstoerung('meldung.stoerung.unerreichbar'))
  }

  // Jede Antwort nennt die Fassung des Servers (R1441).
  await vergleiche(typeof antwort.headers?.get === 'function' ? antwort.headers.get(KOPF) : null)

  if (antwort.status === 401) {
    window.dispatchEvent(new CustomEvent(ABGEMELDET))
    throw new Nichtangemeldet()
  }

  if (antwort.status === 423) {
    throw new KennwortNoetig()
  }

  if (antwort.status === 422) {
    const abbruch = await antwort.json()

    /*
      **Zwei Sorten von 422, und sie sehen verschieden aus.** Der Rechenkern
      schickt einen Textbaustein mit Werten; die Anmeldung und die
      Anwenderpflege schicken einen nackten Schlüssel („kennwort.zukurz").
      Beides ist ein Befund und kein Programmfehler — nur formuliert das eine
      der Kern und das andere die Oberfläche.
    */
    if (typeof abbruch?.meldung === 'string') {
      throw new Fachbefund(abbruch.meldung as Sprachschluessel)
    }

    /*
      **Ein technischer Abbruch sagt es ausdrücklich** (7.2 Abschnitt 30,
      R1003): Die EZB antwortet nicht oder liefert nichts. Er hat Satz und Weg
      wie ein Befund, gehört aber ins Störungsfenster und nicht an den Ort.
      Dasselbe gilt für eine Abweisung der Datenbank, die nur ein
      Programmfehler auslösen kann (R1051) — sie kommt ohne `extern`, und das
      Fenster sagt dann, dass der Server abgewiesen hat.
    */
    if (abbruch?.technisch === true) {
      throw gemeldet(
        new Betriebsstoerung(abbruch.extern === false ? 'meldung.stoerung.abgewiesen' : 'meldung.stoerung.extern', {
          meldung: abbruch.meldung,
          handreichung: abbruch.handreichung ?? null,
        }),
      )
    }

    throw new Rechenabbruch(abbruch.meldung, abbruch.handreichung ?? null)
  }

  if (!antwort.ok) {
    throw await stoerungAus(antwort)
  }

  // **Gespeichert — das Namensverzeichnis kommt neu** (R1736): Was eben
  // angelegt oder umbenannt wurde, steht danach überall mit seinem Namen.
  if (!lesend) {
    meldeGespeichert()
  }

  if (antwort.status === 204) {
    return null as T
  }

  return (await antwort.json()) as T
}

/** Wann eine Handbuchung endet, außer in der Folgeperiode (2.16 Abschnitt 7.3, R1832). */
export type Aufloesungszeitpunkt = 'jahresende' | 'neues-jahr'

export const api = {
  auswahl: () => hole<Auswahl>('/api/auswahl'),

  /**
   * Der Lauf, der für eine Auswahl gilt — ohne zu rechnen.
   *
   * `null`, wenn es für Kreis, Bewertungsebene und Periode keinen gibt. Das ist kein
   * Fehler, sondern die Auskunft: Für diese Auswahl liegen keine Daten vor.
   * Fehlende Läufe holt die Laufvorsorge nach (R294), sobald ein Bericht sie
   * braucht — dieser Ruf hier stößt sie nicht an, er fragt nur.
   */
  aktuellerLauf: (kreis: string, ledger: string, periode: number) => {
    const frage = new URLSearchParams({ kreis, ledger, periode: String(periode) })
    return hole<Rechenantwort | null>(`/api/berichte/lauf?${frage}`)
  },

  /** Was über dem Bericht als Anzeigewährung zur Wahl steht (2.12 Abschnitt 3.21, R994). */
  anzeigewaehrungen: (ort: { kreis?: string | null; bezug?: string | null; periode: number }) => {
    const frage = new URLSearchParams({ periode: String(ort.periode) })
    if (ort.bezug) {
      frage.set('bezug', ort.bezug)
    } else if (ort.kreis) {
      frage.set('kreis', ort.kreis)
    }

    return hole<Anzeigewaehrungen>(`/api/berichte/anzeigewaehrungen?${frage}`)
  },

  /**
   * Einen Abschluss ausdrücklich rechnen — **geparkt** (R295).
   *
   * Der Endpunkt bleibt und trägt weiter die Prüfungen; in der Oberfläche ruft
   * ihn seit dem 2026-09-10 niemand mehr. Er kommt mit dem
   * Konsolidierungsworkflow zurück (2.13), und dann als das, was er dort ist:
   * ein Vorgang mit Berechtigung und Begründung, kein Aktualisierungsknopf.
   */
  rechnen: (kreis: string, ledger: string, periode: number) =>
    hole<Rechenantwort>('/api/abschluss/rechnen', {
      method: 'POST',
      body: JSON.stringify({ kreis, ledger, periode }),
    }),

  /**
   * Bilanz oder GuV eines Laufs — in einer Gliederung.
   *
   * Ohne `plan` die Vorgabe unter den Plänen, die für die Bewertungsebene des Laufs
   * freigegeben sind. Ein Plan, den es dort nicht gibt, wird nicht
   * stillschweigend genommen (2.1).
   */
  /**
   * Bilanz oder GuV aus **gelieferten** Daten — ohne Lauf (R751).
   *
   * Der Bezug ist ein Organisationselement: eine Gesellschaft zeigt sich
   * selbst, eine Gruppe die Summe ihrer Bestandteile (R752). Addiert, nicht
   * konsolidiert — und ohne Umrechnung (R753).
   */
  gelieferteSicht: (bezug: string, ledger: string, periode: number, art: 'bilanz' | 'guv', plan?: string) =>
    hole<Bericht>(
      `/api/berichte/geliefert/${art}?bezug=${encodeURIComponent(bezug)}` +
        `&ledger=${encodeURIComponent(ledger)}&periode=${periode}` +
        (plan ? `&plan=${encodeURIComponent(plan)}` : ''),
    ),

  bericht: (laufId: number, art: 'bilanz' | 'guv', plan?: string) =>
    hole<Bericht>(
      `/api/laeufe/${laufId}/bericht/${art}${plan ? `?plan=${encodeURIComponent(plan)}` : ''}`,
    ),

  /** Die Gliederungen und die Bewertungsebenen, für die sie freigegeben sind. */
  positionsplaene: () => hole<Positionsplan[]>('/api/positionsplaene'),

  /** Die Rollen des Hauses — vier ab Werk, der Vorrat ist offen (R350). */
  rollen: () => hole<Rolle[]>('/api/rollen'),

  /* --- Anmeldung und Anwender (8.1, R355–R364) ---------------------------- */

  /**
   * Wer angemeldet ist, mit welcher Rolle und welchen Rechten (R351, R358).
   *
   * **Die Oberfläche fragt, sie entscheidet nicht.** `null` heißt: niemand —
   * dann zeigt die Anwendung die Anmeldung und keine leere Liste.
   */
  ich: async (): Promise<Angemeldet | null> => {
    try {
      return await hole<Angemeldet>('/api/anwender/ich')
    } catch (fehler) {
      if (fehler instanceof Nichtangemeldet) {
        return null
      }

      throw fehler
    }
  },

  anmelden: (anmeldename: string, kennwort: string) =>
    hole<{ angemeldet: boolean }>('/api/anmeldung', {
      method: 'POST',
      body: JSON.stringify({ anmeldename, kennwort }),
    }),

  abmelden: () => hole<{ angemeldet: boolean }>('/api/abmeldung', { method: 'POST' }),

  kennwortWechseln: (altes: string, neues: string) =>
    hole<{ gewechselt: boolean }>('/api/anwender/kennwort', {
      method: 'POST',
      body: JSON.stringify({ altes, neues }),
    }),

  /** Der eigene Schalter der spielerischen Elemente (7.7, R920). */
  spielschalter: () => hole<Spielschalterangabe>('/api/anwender/ich/spiel'),

  spielschalterSetzen: (schalter: Spielschalterangabe) =>
    hole<Spielschalterangabe>('/api/anwender/ich/spiel', {
      method: 'POST',
      body: JSON.stringify(schalter),
    }),

  /** Die eigene Zahlendarstellung, an jedem Rechner dieselbe (7.2 Abschnitt 43, R1315). */
  zahlendarstellung: () => hole<Zahlendarstellungangabe>('/api/anwender/ich/zahlendarstellung'),

  zahlendarstellungSetzen: (wahl: Omit<Zahlendarstellungangabe, 'gewaehlt'>) =>
    hole<Zahlendarstellungangabe>('/api/anwender/ich/zahlendarstellung', {
      method: 'POST',
      body: JSON.stringify(wahl),
    }),

  /** Was der Angemeldete überwacht, und das Übersichtsblatt dazu (7.1 Abschnitt 12, R1512–R1519). */
  ueberwachung: () => hole<Ueberwachungsstand>('/api/arbeitsliste/ueberwachung'),

  ueberwachungFestlegen: (festlegung: Ueberwachungsfestlegung) =>
    hole<Ueberwachungsstand>('/api/arbeitsliste/ueberwachung', {
      method: 'POST',
      body: JSON.stringify(festlegung),
    }),

  /** Ob dieser Erfolgsmoment jetzt zu zeigen ist — einmal je Anlass (R922). */
  erfolgsmoment: (frage: Erfolgsmomentfrage) =>
    hole<{ zeigen: boolean }>('/api/anwender/ich/erfolgsmoment', {
      method: 'POST',
      body: JSON.stringify(frage),
    }),

  /** Serie und Bestzeit des Kreises, aus dem Bestand errechnet (R923). */
  serie: (kreis: string, ledger: string) =>
    hole<Seriestand>(
      `/api/anwender/ich/serie?kreis=${encodeURIComponent(kreis)}&ledger=${encodeURIComponent(ledger)}`,
    ),

  anwenderliste: () => hole<Anwenderangabe[]>('/api/anwender'),

  verben: () => hole<string[]>('/api/anwender/verben'),

  /** Was diese Rolle beim Anlegen vorschlägt — ein Vorschlag, keine Bindung (R358). */
  rechtevorschlag: (rolle: string) =>
    hole<string[]>(`/api/anwender/vorschlag/${encodeURIComponent(rolle)}`),

  anwenderAnlegen: (auftrag: Anwenderauftrag) =>
    hole<{ schluessel: string }>('/api/anwender', {
      method: 'POST',
      body: JSON.stringify(auftrag),
    }),

  anwenderPflegen: (schluessel: string, auftrag: Anwenderauftrag) =>
    hole<{ schluessel: string }>(`/api/anwender/${encodeURIComponent(schluessel)}`, {
      method: 'POST',
      body: JSON.stringify(auftrag),
    }),

  /** Die angelegten Abschlüsse mit Zustand und Prüfcode — die erste Kachel des Prüfers (R353). */
  abschluesse: () => hole<Abschluss[]>('/api/abschluesse'),

  vorgaenge: (laufId: number) => hole<Vorgangskopf[]>(`/api/laeufe/${laufId}/vorgaenge`),

  /**
   * Die Buchungen eines Laufs — alle, oder die eines Vorgangs.
   *
   * Ohne `vorgangId` liefert die API den ganzen Lauf; das braucht das
   * Buchungsjournal, das über alle Belegklammern hinweg sucht.
   */
  buchungen: (laufId: number, vorgangId?: number) =>
    hole<Buchungszeile[]>(
      `/api/laeufe/${laufId}/buchungen${vorgangId === undefined ? '' : `?vorgang=${vorgangId}`}`,
    ),

  freigabepruefung: (laufId: number) =>
    hole<Freigabeurteil>(`/api/laeufe/${laufId}/freigabepruefung`),

  /**
   * Gibt frei — und liefert das Urteil auch dann, wenn die Freigabe abgewiesen wird.
   *
   * Die abgewiesene Freigabe kommt als 409 mit demselben Satz zurück. Das ist
   * kein Fehlerfall, über den die Oberfläche stolpern dürfte, sondern die
   * Antwort auf die gestellte Frage: Sie enthält die Befunde, die der Freigabe
   * entgegenstehen, samt Handreichung.
   */
  freigeben: async (
    laufId: number,
    bemerkung: string | null,
    /** Je Hinweis ein Satz, warum trotzdem freigegeben wird (R912). */
    begruendungen: Record<string, string> = {},
  ): Promise<Freigabeurteil> => {
    let antwort: Response
    try {
      antwort = await rufe(`/api/laeufe/${laufId}/freigabe`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Coko-Sprache': aktuelleSprache() },
        body: JSON.stringify({ bemerkung, begruendungen }),
      })
    } catch {
      throw gemeldet(new Betriebsstoerung('meldung.stoerung.unerreichbar'))
    }

    if (antwort.ok || antwort.status === 409) {
      return (await antwort.json()) as Freigabeurteil
    }

    if (antwort.status === 401) {
      window.dispatchEvent(new CustomEvent(ABGEMELDET))
      throw new Nichtangemeldet()
    }

    if (antwort.status === 422) {
      const abbruch = await antwort.json()
      throw new Rechenabbruch(abbruch.meldung, abbruch.handreichung ?? null)
    }

    // Bis zum 2026-10-04 ein bloßer Fehler ohne Fänger: Die Freigabe schlug
    // fehl, und am Bildschirm geschah nichts (R1003).
    throw await stoerungAus(antwort)
  },

  /** Welche Monate eines Kreises geschlossen sind (2.13 Abschnitt 20.7, R1690). */
  periodenschluesse: (kreis: string) =>
    hole<Periodenschluss[]>(`/api/abschluss/periodenschluss?kreis=${encodeURIComponent(kreis)}`),

  /** Schließt einen Monat eines Kreises, mit Begründung (R1690). */
  periodeSchliessen: (kreis: string, periode: number, begruendung: string) =>
    hole<Periodenschluss[]>('/api/abschluss/periodenschluss/schliessen', {
      method: 'POST',
      body: JSON.stringify({ kreis, periode, begruendung }),
    }),

  /** Öffnet einen geschlossenen Monat wieder, mit Begründung (R1692). */
  periodeWiedereroeffnen: (kreis: string, periode: number, begruendung: string) =>
    hole<Periodenschluss[]>('/api/abschluss/periodenschluss/wiedereroeffnen', {
      method: 'POST',
      body: JSON.stringify({ kreis, periode, begruendung }),
    }),

  /** Welche Monate ihre Freigabe verlören — vor dem Bestätigen (R1693). */
  freigabeZuruecknahme: (kreis: string, ledger: string, periode: number) =>
    hole<number[]>(
      `/api/abschluss/freigabe/zuruecknahme?kreis=${encodeURIComponent(kreis)}&ledger=${encodeURIComponent(ledger)}&periode=${periode}`,
    ),

  /** Nimmt die Freigabe dieses und jedes späteren freigegebenen Monats zurück (R1693). */
  freigabeZuruecknehmen: (kreis: string, ledger: string, periode: number, begruendung: string) =>
    hole<number[]>('/api/abschluss/freigabe/zuruecknahme', {
      method: 'POST',
      body: JSON.stringify({ kreis, ledger, periode, begruendung }),
    }),

  nachweispruefung: () => hole<Nachweispruefung>('/api/nachweis/pruefung'),

  messung: () => hole<Messwerte>('/api/messung'),

  /**
   * Der Regelnachweis der Prüferansicht (2.13 Abschnitt 9).
   *
   * **Ohne Auswahl.** Der erste Teil mißt den ganzen Bestand, der zweite
   * rechnet einen erfundenen Lehrkonzern vor — beides hängt an nichts, was
   * der Prüfer vorher einstellen müßte.
   */
  prueferansicht: () => hole<Regelnachweis>('/api/prueferansicht'),

  /**
   * Bilanz oder GuV über mehrere Perioden nebeneinander.
   *
   * Ein eigener Endpunkt und nicht ein Parameter am Berichtsendpunkt: Je
   * Spalte steht ein anderer Lauf mit eigenem Prüfcode. Das ist ein anderer
   * Bericht, nicht dieselbe Ansicht mit anderen Spalten.
   */
  /**
   * Die Perioden, zu denen es für Kreis und Bewertungsebene einen Lauf gibt — die
   * Spannweite des Zeitschiebers (7.3 Abschnitt 2g).
   */
  /**
   * Aus welcher Welt ein Bericht eines Kreises kommt (2.13 Abschnitt 14, R1114)
   * — Konzernabschluss, Rechenzeitpunkt, was seither geändert ist.
   */
  berichtswelt: (kreis: string, ledger: string, von: number, bis: number) => {
    const frage = new URLSearchParams({ kreis, ledger, von: String(von), bis: String(bis) })
    return hole<Berichtswelt>(`/api/berichte/welt?${frage}`)
  },

  laufperioden: (kreis: string, ledger: string) => {
    const frage = new URLSearchParams({ kreis, ledger })
    return hole<number[]>(`/api/berichte/laufperioden?${frage}`)
  },

  /**
   * Bilanz oder GuV über die Zeiteinheiten von `von` bis `bis` — genau die
   * gewählte Zeit, im Raster der Kontextzeile.
   */
  periodenbericht: (
    kreis: string,
    ledger: string,
    art: 'bilanz' | 'guv',
    raster: string,
    bis: number,
    von?: number,
    /** Year to date (R427): je Spalte alles vom Geschäftsjahresanfang bis zu ihrem Ende. */
    kumuliert = false,
  ) => {
    const frage = new URLSearchParams({ kreis, ledger, art, raster, bis: String(bis) })
    if (von) frage.set('von', String(von))
    if (kumuliert) frage.set('kumuliert', 'true')
    return hole<Periodenbericht>(`/api/berichte/perioden?${frage}`)
  },

  /**
   * Derselbe Zeitvergleich **ohne Lauf** — für die gelieferte Sicht (R759).
   *
   * Je Spalte eine Lesung des Journals. Ein Monat ohne Lieferung steht mit
   * Null da: Der Zeitraum gibt die Spalten vor, nicht der Datenbestand.
   */
  gelieferteperioden: (
    bezug: string,
    ledger: string,
    art: 'bilanz' | 'guv',
    raster: string,
    bis: number,
    von?: number,
    kumuliert = false,
    plan?: string | null,
  ) => {
    const frage = new URLSearchParams({ bezug, ledger, art, raster, bis: String(bis) })
    if (von) frage.set('von', String(von))
    if (kumuliert) frage.set('kumuliert', 'true')
    if (plan) frage.set('plan', plan)
    return hole<Periodenbericht>(`/api/berichte/geliefert/perioden?${frage}`)
  },

  /**
   * Wie eine Zahl entstanden ist — Anfangsbestand plus Bewegungen (2.14).
   *
   * **Der ganze Filter der Zelle reist mit** (R205), die Konsolidierungsspalte
   * eingeschlossen: Ohne sie beantwortet der Nachweis eine andere Frage als die
   * gestellte. Gibt es für die Auswahl keinen Lauf, antwortet die API mit
   * `204` — dann steht kein Nachweis zur Verfügung und nicht etwa ein leerer.
   */
  wertenachweis: (frage: {
    kreis: string
    ledger: string
    art: 'bilanz' | 'guv'
    periode: number
    position: string
    konto?: string | null
    einheit?: string | null
    spalte: string
    weitere?: Record<string, string | null>
    spiegelblock?: string | null
    spiegelspalte?: string | null
  }) => {
    const suchteil = new URLSearchParams({
      kreis: frage.kreis,
      ledger: frage.ledger,
      art: frage.art,
      periode: String(frage.periode),
      position: frage.position,
      spalte: frage.spalte,
    })

    if (frage.konto) {
      suchteil.set('konto', frage.konto)
    }

    if (frage.einheit) {
      suchteil.set('einheit', frage.einheit)
    }

    // Der Filter reist vollständig mit (R205) — auch der Teil, den erst der
    // Aufriss festgelegt hat, und die Spiegelspalte einer Spiegelzelle.
    mitWeiteren(suchteil, frage.weitere)
    if (frage.spiegelspalte) {
      suchteil.set('spiegelblock', frage.spiegelblock ?? 'bestand')
      suchteil.set('spiegelspalte', frage.spiegelspalte)
    }

    return hole<WertenachweisSatz | null>(`/api/berichte/wertenachweis?${suchteil}`)
  },

  /**
   * Derselbe Nachweis ohne Lauf — für die gelieferte Sicht (R771).
   *
   * **Der Bezug ist ein Organisationselement** (R752), kein Kreis: Wo der
   * gerechnete Nachweis nach dem Lauf fragt, fragt dieser nach den gelieferten
   * Zahlen selbst. Wurde im ganzen Geschäftsjahr nichts geliefert, antwortet
   * die API mit `204`.
   */
  gelieferterNachweis: (frage: {
    bezug: string
    ledger: string
    art: 'bilanz' | 'guv'
    periode: number
    position: string
    konto?: string | null
    einheit?: string | null
    spalte: string
    weitere?: Record<string, string | null>
    spiegelblock?: string | null
    spiegelspalte?: string | null
  }) => {
    const suchteil = new URLSearchParams({
      bezug: frage.bezug,
      ledger: frage.ledger,
      art: frage.art,
      periode: String(frage.periode),
      position: frage.position,
      spalte: frage.spalte,
    })

    if (frage.konto) {
      suchteil.set('konto', frage.konto)
    }

    if (frage.einheit) {
      suchteil.set('einheit', frage.einheit)
    }

    mitWeiteren(suchteil, frage.weitere)
    if (frage.spiegelspalte) {
      suchteil.set('spiegelblock', frage.spiegelblock ?? 'bestand')
      suchteil.set('spiegelspalte', frage.spiegelspalte)
    }

    return hole<WertenachweisSatz | null>(`/api/berichte/geliefert/wertenachweis?${suchteil}`)
  },

  /* --- Der Aufriss auf Abruf (7.3 Abschnitt 2f) ------------------------- */

  /** Welche Zusatzachsen diese Läufe anbieten — und ob sie belegt sind. */
  achsen: (laeufe: number[]) =>
    laeufe.length === 0
      ? Promise.resolve([] as Achsenangabe[])
      : hole<Achsenangabe[]>(`/api/berichte/achsen?laeufe=${laeufe.join(',')}`),

  /**
   * Der Aufriss einer Berichtszeile nach einer Zusatzachse — ohne `position`
   * für alle Zeilen, das ist das Spaltenbild *Partner*.
   */
  aufriss: (frage: {
    lauf: number
    art: 'bilanz' | 'guv'
    plan?: string | null
    position?: string | null
    achse: string
    einheit?: string | null
    konto?: string | null
    weitere?: Record<string, string | null>
  }) => {
    const teil = new URLSearchParams({ lauf: String(frage.lauf), art: frage.art, achse: frage.achse })
    if (frage.plan) teil.set('plan', frage.plan)
    if (frage.position) teil.set('position', frage.position)
    if (frage.einheit) teil.set('einheit', frage.einheit)
    if (frage.konto) teil.set('konto', frage.konto)
    return hole<Zusatzaufriss>(`/api/berichte/aufriss?${mitWeiteren(teil, frage.weitere)}`)
  },

  /**
   * Derselbe Aufriss **ohne Lauf** — für die gelieferte Sicht (R759).
   *
   * *Gegen wen habe ich Forderungen* ist eine Frage an die Gesellschaft, die
   * liefert, und keine Konzernzahl. Gelesen wird dieselbe Quelle wie für die
   * sechs Berichte (R754).
   */
  gelieferterAufriss: (frage: {
    bezug: string
    ledger: string
    periode: number
    art: 'bilanz' | 'guv'
    plan?: string | null
    position?: string | null
    achse: string
    einheit?: string | null
    konto?: string | null
    weitere?: Record<string, string | null>
  }) => {
    const teil = new URLSearchParams({
      bezug: frage.bezug,
      ledger: frage.ledger,
      periode: String(frage.periode),
      art: frage.art,
      achse: frage.achse,
    })
    if (frage.plan) teil.set('plan', frage.plan)
    if (frage.position) teil.set('position', frage.position)
    if (frage.einheit) teil.set('einheit', frage.einheit)
    if (frage.konto) teil.set('konto', frage.konto)
    return hole<Zusatzaufriss>(`/api/berichte/geliefert/aufriss?${mitWeiteren(teil, frage.weitere)}`)
  },

  periodenAufriss: (frage: {
    kreis: string
    ledger: string
    art: 'bilanz' | 'guv'
    raster: string
    bis: number
    position: string
    /** Die Stelle der Zeile (R1723) — nur bei einer Position, die im Zeitraum umgehängt wurde. */
    stelle?: string
    achse: string
    einheit?: string | null
    konto?: string | null
    weitere?: Record<string, string | null>
    anzahl?: number
    von?: number
    kumuliert?: boolean
  }) => {
    const teil = new URLSearchParams({
      kreis: frage.kreis,
      ledger: frage.ledger,
      art: frage.art,
      raster: frage.raster,
      bis: String(frage.bis),
      anzahl: String(frage.anzahl ?? 8),
      position: frage.position,
      achse: frage.achse,
    })
    if (frage.einheit) teil.set('einheit', frage.einheit)
    if (frage.konto) teil.set('konto', frage.konto)
    if (frage.von) teil.set('von', String(frage.von))
    // Der Aufriss sieht dieselbe Zeitreihe wie die Zeile darüber (R180).
    if (frage.kumuliert) teil.set('kumuliert', 'true')
    // Eine umgehängte Position klappt nur ihre Monate an dieser Stelle auf (R1723).
    if (frage.stelle !== undefined) teil.set('stelle', frage.stelle)
    return hole<Periodenzusatzaufriss>(`/api/berichte/perioden/aufriss?${mitWeiteren(teil, frage.weitere)}`)
  },

  spiegelAufriss: (frage: {
    kreis: string
    ledger: string
    bis: number
    art: string
    position: string
    achse: string
    einheit?: string | null
    konto?: string | null
    weitere?: Record<string, string | null>
  }) => {
    const teil = new URLSearchParams({
      kreis: frage.kreis,
      ledger: frage.ledger,
      bis: String(frage.bis),
      art: frage.art,
      position: frage.position,
      achse: frage.achse,
    })
    if (frage.einheit) teil.set('einheit', frage.einheit)
    if (frage.konto) teil.set('konto', frage.konto)
    return hole<Spiegelzusatzaufriss>(`/api/berichte/spiegel/aufriss?${mitWeiteren(teil, frage.weitere)}`)
  },

  kapitalflussAufriss: (frage: {
    kreis: string
    ledger: string
    bis: number
    raster: string
    waehrungsdelta: boolean
    zeile?: string | null
    achse: string
    einheit?: string | null
    konto?: string | null
    weitere?: Record<string, string | null>
    von?: number
    /** Die gewählte Gesellschaft (R759) — gerechnet aus ihren gelieferten Daten. */
    bezug?: string
  }) => {
    const teil = new URLSearchParams({
      kreis: frage.kreis,
      ledger: frage.ledger,
      bis: String(frage.bis),
      raster: frage.raster,
      waehrungsdelta: String(frage.waehrungsdelta),
      achse: frage.achse,
    })
    if (frage.zeile) teil.set('zeile', frage.zeile)
    if (frage.einheit) teil.set('einheit', frage.einheit)
    if (frage.konto) teil.set('konto', frage.konto)
    if (frage.von) teil.set('von', String(frage.von))
    if (frage.bezug) teil.set('bezug', frage.bezug)
    return hole<Kfrzusatzaufriss>(`/api/berichte/kapitalfluss/aufriss?${mitWeiteren(teil, frage.weitere)}`)
  },

  kapitalflussPeriodenAufriss: (frage: {
    kreis: string
    ledger: string
    raster: string
    bis: number
    waehrungsdelta: boolean
    zeile: string
    achse: string
    einheit?: string | null
    konto?: string | null
    weitere?: Record<string, string | null>
    anzahl?: number
    von?: number
    kumuliert?: boolean
    /** Die gewählte Gesellschaft (R759) — je Spalte aus ihren gelieferten Daten. */
    bezug?: string
  }) => {
    const teil = new URLSearchParams({
      kreis: frage.kreis,
      ledger: frage.ledger,
      raster: frage.raster,
      bis: String(frage.bis),
      anzahl: String(frage.anzahl ?? 8),
      waehrungsdelta: String(frage.waehrungsdelta),
      zeile: frage.zeile,
      achse: frage.achse,
    })
    if (frage.einheit) teil.set('einheit', frage.einheit)
    if (frage.konto) teil.set('konto', frage.konto)
    if (frage.von) teil.set('von', String(frage.von))
    if (frage.kumuliert) teil.set('kumuliert', 'true')
    if (frage.bezug) teil.set('bezug', frage.bezug)
    return hole<Kfrperiodenzusatzaufriss>(
      `/api/berichte/kapitalfluss/perioden/aufriss?${mitWeiteren(teil, frage.weitere)}`,
    )
  },

  /**
   * Die Belege des Journals, juengste zuerst (2.14).
   *
   * Ohne Filter die letzten hundert; mit Filter das, womit der Anwender
   * hereingekommen ist.
   */
  journal: (frage: {
    ledger?: string
    periode?: number
    einheit?: string
    belegart?: string
    konto?: string
    /** Genau dieser Beleg, ohne jeden anderen Filter — der Link aus dem Nachweis (R209). */
    beleg?: number
    hoechstens?: number
  }) => {
    const teil = new URLSearchParams()
    for (const [name, wert] of Object.entries(frage)) {
      if (wert !== undefined && wert !== '') {
        teil.set(name, String(wert))
      }
    }

    return hole<Belegkopf[]>(`/api/journal?${teil}`)
  },

  /** Die Zeilen eines Belegs. */
  belegzeilen: (belegId: number) => hole<Belegzeile[]>(`/api/journal/${belegId}`),

  // ---- Handerfassung und Belegordner (2.16) --------------------------

  /**
   * Was die Buchungsmaske braucht, bevor eine Zahl darin steht.
   *
   * **Ein Ruf, und die Maske steht** — Konten mit den darauf zulässigen
   * Bewegungsarten (R371), Partner, Segmente, Funktionsbereiche und die
   * Analysedimensionen mit ihren Ausprägungen. Ein halbes Dutzend
   * Stammdatenabfragen beim Öffnen wäre eine Maske, die nach dem Rechtsklick
   * erst einmal blinkt.
   */
  buchungsrahmen: (einheit: string, ledger: string, periode: number) =>
    hole<Buchungsrahmen>(
      `/api/erfassung/rahmen?${new URLSearchParams({
        einheit,
        ledger,
        periode: String(periode),
      })}`,
    ),

  /** Bucht einen Beleg von Hand (R366). */
  buchen: (auftrag: Erfassungsauftrag) =>
    hole<{ nummer: string }>('/api/erfassung', {
      method: 'POST',
      body: JSON.stringify(auftrag),
    }),

  /**
   * Berichtigt einen Erfassungsbeleg (R388).
   *
   * **Der Beleg behält Nummer und Kennung.** Gesellschaft, Bewertungsebene und Periode
   * im Auftrag werden vom Dienst überschrieben — sie kommen vom Beleg; wer sie
   * verschieben will, macht einen anderen Beleg daraus.
   */
  belegAendern: (belegId: number, auftrag: Erfassungsauftrag) =>
    hole<null>(`/api/erfassung/${belegId}/aendern`, {
      method: 'POST',
      body: JSON.stringify(auftrag),
    }),

  /**
   * Setzt die Reihenfolge der Belege eines Ordners von Hand (R392).
   *
   * **Die vollständige Reihenfolge, nicht eine einzelne Verschiebung:** Wer
   * Zeile drei nach oben zieht, ändert die Stellung aller Zeilen darüber.
   */
  belegreihenfolge: (ordnerId: number, belege: number[]) =>
    hole<null>(`/api/journal/ordner/${ordnerId}/reihenfolge`, {
      method: 'POST',
      body: JSON.stringify(belege),
    }),

  /** Löst einen Beleg in der Folgeperiode auf — kein Storno (R383). */
  /** Wann die Handbuchung endet (2.16 Abschnitt 7.3, R1832); ohne Angabe die Folgeperiode. */
  umkehren: (belegId: number, zeitpunkt?: Aufloesungszeitpunkt) =>
    hole<{ nummer: string }>(`/api/erfassung/${belegId}/umkehren?${new URLSearchParams(zeitpunkt ? { zeitpunkt } : {})}`, {
      method: 'POST',
    }),

  /** Was beim Löschen auf dem Spiel steht (R380). */
  loeschvorschau: (belegId: number) =>
    hole<Loeschvorschau>(`/api/erfassung/${belegId}/loeschvorschau`),

  /** Löscht einen Erfassungsbeleg — wirklich (R377). */
  belegLoeschen: (belegId: number) =>
    hole<null>(`/api/erfassung/${belegId}`, { method: 'DELETE' }),

  /** Das Änderungsprotokoll eines Belegs (R378). */
  belegprotokoll: (belegId: number) =>
    hole<Protokolleintrag[]>(`/api/erfassung/${belegId}/protokoll`),

  /** Die erklärten Lücken im Belegkreis (R379). */
  geloeschteBelege: (ledger?: string, periode?: number) => {
    const teil = new URLSearchParams()
    if (ledger) teil.set('ledger', ledger)
    if (periode) teil.set('periode', String(periode))
    return hole<Protokolleintrag[]>(`/api/journal/geloescht?${teil}`)
  },

  /**
   * Text- und Wertsuche im Journal (2.14 Abschnitt 7, H10).
   *
   * **Die Oberfläche entscheidet, wonach gesucht wird** — mit derselben
   * Lesung wie die Berichtssuche (`wertLesen`): Ziffern suchen Beträge,
   * Buchstaben suchen Text. Der Anwender soll das nicht vorher entscheiden
   * müssen; wer eine Differenz jagt, tippt sie ein.
   *
   * **Begrenzt auf eine Gesellschaft.** H10 in voller Breite — alle
   * Gesellschaften, alle Perioden — ist der Spaltenscan, für den 2.1 und 2.12
   * den Parquet-Schnitt aus Bereich 4 verlangen.
   */
  journalsuche: (
    einheit: string,
    frage: { text?: string; wert?: number; toleranz?: number },
  ) => {
    const teil = new URLSearchParams({ einheit })
    if (frage.text) teil.set('text', frage.text)
    if (frage.wert !== undefined) {
      teil.set('wert', String(frage.wert))
      teil.set('toleranz', String(frage.toleranz ?? 0.005))
    }
    return hole<Journaltreffer[]>(`/api/journal/suche?${teil}`)
  },

  /** Der Ordnerbaum einer Gesellschaft — je Geschäftsjahr ein Jahresordner, darunter die eigenen (R375, R984). */
  belegordner: (einheit: string, ledger?: string, periode?: number | null) => {
    const teil = new URLSearchParams({ einheit })
    if (ledger) teil.set('ledger', ledger)
    // Die Periode des Journals zählt nur diesen Monat; der Baum bleibt (R986).
    if (periode) teil.set('periode', String(periode))
    return hole<Belegordner[]>(`/api/journal/ordner?${teil}`)
  },

  /**
   * Die Belege eines Ordners.
   *
   * **Der Jahresordner trägt, was kein eigener Ordner hält** (R984) — jeden
   * Beleg seines Geschäftsjahres, über alle Perioden, begrenzt auf die
   * Bewertungsebene. Was in einem eigenen Ordner liegt, steht ohne diese
   * Grenze da: Dorthin hat es ein Mensch gelegt.
   */
  ordnerbelege: (ordnerId: number, ledger?: string, periode?: number | null) => {
    const teil = new URLSearchParams()
    if (ledger) teil.set('ledger', ledger)
    if (periode) teil.set('periode', String(periode))
    return hole<Ordnerbeleg[]>(`/api/journal/ordner/${ordnerId}?${teil}`)
  },

  /** Hängt einen Ordner woandershin — in seiner Gesellschaft und seinem Jahr (R983, R985). */
  ordnerVerschieben: (ordnerId: number, eltern: number) =>
    hole<null>(`/api/journal/ordner/${ordnerId}/verschieben?${new URLSearchParams({ eltern: String(eltern) })}`, {
      method: 'POST',
    }),

  ordnerAnlegen: (einheit: string, name: string, eltern?: number) => {
    const teil = new URLSearchParams({ einheit, name })
    if (eltern !== undefined) teil.set('eltern', String(eltern))
    return hole<{ id: number }>(`/api/journal/ordner?${teil}`, { method: 'POST' })
  },

  ordnerUmbenennen: (ordnerId: number, name: string) =>
    hole<null>(`/api/journal/ordner/${ordnerId}/umbenennen?${new URLSearchParams({ name })}`, {
      method: 'POST',
    }),

  ordnerLoeschen: (ordnerId: number) =>
    hole<null>(`/api/journal/ordner/${ordnerId}`, { method: 'DELETE' }),

  /**
   * Verschiebt einen Beleg in einen Ordner (Strg+X, Strg+V).
   *
   * **Fasst den Beleg nicht an** (R375) — die Ablage liegt in einer eigenen
   * Tabelle, und eine Ablagefrage weicht die Schreibregel des Journals nicht
   * auf.
   */
  belegAblegen: (beleg: number, ordner: number) =>
    hole<null>(
      `/api/journal/ablage?${new URLSearchParams({
        beleg: String(beleg),
        ordner: String(ordner),
      })}`,
      { method: 'POST' },
    ),

  /**
   * Die indirekte Kapitalflussrechnung nach DRS 21 (2.8, G3).
   *
   * **Ein Zeitraum, kein Stichtag.** Eine Kapitalflussrechnung ist eine
   * Stromgröße; `raster` sagt, wie weit sie zurückreicht — dieselben drei
   * Raster wie im Periodenbild.
   *
   * `waehrungsdelta` entscheidet, ob die Stichtagskursanpassung **gesondert
   * ausgewiesen** wird. Weggelassen wird sie nie: Die Rechnung geht in beiden
   * Stellungen auf, sie ist nur anders gegliedert.
   */
  kapitalfluss: (
    kreis: string,
    ledger: string,
    bis: number,
    raster: string,
    waehrungsdelta: boolean,
    von?: number,
    /** Mit Bezug die gelieferte Sicht (R751) — gerechnet wird trotzdem. */
    bezug?: string,
  ) => {
    const frage = new URLSearchParams({
      kreis,
      ledger,
      bis: String(bis),
      raster,
      waehrungsdelta: String(waehrungsdelta),
    })
    if (bezug) frage.set('bezug', bezug)
    // Der Zeitraum kommt aus dem Zeitschieber; ohne ihn spannt das Raster
    // ihn auf, wie bisher.
    if (von) frage.set('von', String(von))
    return hole<Kapitalflussrechnung>(`/api/berichte/kapitalfluss?${frage}`)
  },

  /**
   * Die Kapitalflussrechnung über mehrere Zeiträume nebeneinander.
   *
   * **Ein eigener Endpunkt und kein Parameter am Rechnungsendpunkt** — genau
   * wie beim Periodenbericht: Je Spalte steht eine andere Rechnung mit eigenem
   * Anfangsbestand und eigenem Prüfcode. Das ist eine andere Auswertung, nicht
   * dieselbe Ansicht mit anderen Spalten.
   */
  kapitalflussPerioden: (
    kreis: string,
    ledger: string,
    raster: string,
    bis: number,
    waehrungsdelta: boolean,
    von?: number,
    anzahl = 8,
    /** Year to date (R427): jede Spalte rechnet ab dem Geschäftsjahresbeginn. */
    kumuliert = false,
    /**
     * Die gewählte Gesellschaft (R759): je Spalte die gelieferte Sicht. Bis
     * zum 2026-10-04 fehlte er hier, und das Bild zeigte unter „Holding AG"
     * den Konzern.
     */
    bezug?: string,
  ) => {
    const frage = new URLSearchParams({
      kreis,
      ledger,
      raster,
      bis: String(bis),
      anzahl: String(anzahl),
      waehrungsdelta: String(waehrungsdelta),
    })
    if (von) frage.set('von', String(von))
    if (kumuliert) frage.set('kumuliert', 'true')
    if (bezug) frage.set('bezug', bezug)
    return hole<Kfrperiodenbericht>(`/api/berichte/kapitalfluss/perioden?${frage}`)
  },


  /**
   * Wie eine Zahl der Kapitalflussrechnung entstanden ist (2.8 Abschnitt
   * 13.14, R267–R270).
   *
   * **Nicht die Form des Bilanznachweises.** Dort steht Anfangsbestand plus
   * Bewegungen; eine KFR-Zeile hat keinen Anfangsbestand, weil sie über
   * mehrere Konten läuft — sie ist selbst schon eine Bewegungsrechnung. Der
   * Nachweis zeigt, was sie aufgenommen hat, je Gesellschaft, Konto und
   * Bewegungsart, mit der Herkunft.
   *
   * **`von` schlägt das Raster**: Im Periodenbild hat jede Spalte ihren
   * eigenen Zeitraum, und der steht nicht im Raster, sondern in der Spalte.
   */
  kapitalflussNachweis: (frage: {
    kreis: string
    ledger: string
    bis: number
    von?: number | null
    raster: string
    waehrungsdelta: boolean
    zeile: string
    einheit?: string | null
    konto?: string | null
    spalte: string
    weitere?: Record<string, string | null>
    /**
     * Der Bezug der **gelieferten** Sicht (R751, R771) — gesetzt heißt: ohne
     * Lauf, gerechnet aus dem Journal. `kreis` steht dann leer, denn es gibt
     * keinen.
     */
    bezug?: string
  }) => {
    const teil = mitWeiteren(
      new URLSearchParams({
        kreis: frage.kreis,
        ledger: frage.ledger,
        bis: String(frage.bis),
        raster: frage.raster,
        waehrungsdelta: String(frage.waehrungsdelta),
        zeile: frage.zeile,
        spalte: frage.spalte,
      }),
      frage.weitere,
    )

    if (frage.von) {
      teil.set('von', String(frage.von))
    }

    if (frage.einheit) {
      teil.set('einheit', frage.einheit)
    }

    if (frage.konto) {
      teil.set('konto', frage.konto)
    }

    // **Der Bezug entscheidet, welche Sicht gilt** (R752) — derselbe Griff wie
    // bei der Rechnung selbst. Ein Endpunkt, zwei Herkünfte.
    if (frage.bezug) {
      teil.set('bezug', frage.bezug)
    }

    return hole<Kfrnachweis | null>(`/api/berichte/kapitalfluss/nachweis?${teil}`)
  },

  /** Anlagen-, Eigenkapital- oder Kreditspiegel bis zur gewählten Periode. */
  /**
   * Anlagen-, Eigenkapital- oder Kreditspiegel.
   *
   * **Mit `bezug` kommt die gelieferte Sicht** (R751, R752): dieselben
   * Bewegungen, die auch Bilanz und GuV zeigen — ohne Lauf.
   */
  spiegel: (kreis: string, ledger: string, bis: number, art: string, bezug?: string) => {
    const frage = new URLSearchParams({ kreis, ledger, bis: String(bis), art })
    if (bezug) frage.set('bezug', bezug)
    return hole<Spiegel>(`/api/berichte/spiegel?${frage}`)
  },

  /**
   * Der Stammdatenkatalog — Kennung, Code und alle Sprachen je Konto.
   *
   * Einmal je Sitzung geholt: Er ändert sich nicht, während jemand einen
   * Abschluss ansieht, und ein Sprachwechsel darf ihn nicht neu laden.
   */
  stammdatenkatalog: () => hole<Stammdatenkatalog>('/api/stammdatenkatalog'),

  /* ---- Stammdatenpflege (7.4) ---------------------------------------- */

  /**
   * Der feste Vorrat an Objektarten und ihr Feldervorrat (R235).
   *
   * **Die Oberfläche kennt die Objektarten nicht.** Sie baut ihre Maske aus
   * dieser Antwort — sonst wäre jedes neunte Objekt eine Änderung an zwei
   * Stellen, und eine davon würde vergessen.
   */
  stammdatenarten: () => hole<Objektartbeschreibung[]>('/api/stammdaten/arten'),

  /**
   * Welche Objektart eine Organisationsart meint (2.3 Abschnitt 10, R712).
   *
   * `{ gesellschaft: 'einheit', kreis: 'konsolidierungskreis', … }` — die
   * Oberfläche holt damit den richtigen Auswahlvorrat, wenn ein
   * Organisationselement aufgenommen wird. **Nicht als Konstante hier:** Die
   * Abbildung ist eine Aussage des Modells und steht an einem Ort (R240).
   */
  organisationsarten: () => hole<Record<string, string>>('/api/stammdaten/organisationsarten'),

  /**
   * Welche Sprachen das Haus erklärt hat (R556) — in der Reihenfolge der
   * Rückfallkette.
   */
  sprachregister: () => hole<Haussprache[]>('/api/sprachen'),

  stammdatenliste: (objektart: string) =>
    hole<Stammdatenliste>(`/api/stammdaten/${encodeURIComponent(objektart)}`),

  /**
   * Ein Stammdatenblatt — heute oder zu einem alten Stand (R241, R517).
   *
   * **Ohne `stand` ist es der heutige Blick.** Mit `stand` liefert die
   * Schnittstelle das Blatt, wie es nach dieser Erfassungsnummer dastand:
   * Beschriftungen, Eigenschaften, Herkünfte und die Fassungsreihe bis dahin.
   * Der Stand ist eine Erfassungsnummer und kein Datum — die Nummer ist die
   * Ordnung, in der CoKo seine Schreibvorgänge zählt, und nur sie trennt zwei
   * Änderungen derselben Sekunde.
   */
  stammdatenblatt: (objektart: string, schluessel: string, stand?: number | null) =>
    hole<Stammdatenblatt>(
      `/api/stammdaten/${encodeURIComponent(objektart)}/${encodeURIComponent(schluessel)}` +
        (stand == null ? '' : `?stand=${stand}`),
    ),

  /**
   * Legt ein Stammdatum an (7.4, geschlossen am 2026-09-11).
   *
   * Der Schlüssel entsteht aus Vorsilbe und Code und wird nicht übergeben: Ein
   * frei gesetzter Schlüssel wird irgendwann zweimal gesetzt, und korrigieren
   * lässt er sich in einem append-only-Modell nicht.
   */
  stammdatumAnlegen: (objektart: string, code: string, auftrag: Pflegeauftrag) =>
    hole<Pflegeergebnis>(
      `/api/stammdaten/${encodeURIComponent(objektart)}?code=${encodeURIComponent(code)}`,
      { method: 'POST', body: JSON.stringify(auftrag) },
    ),

  /** Wo ein Stammdatum vorkommt — die Frage vor dem Löschen (R316). */
  verwendung: (objektart: string, schluessel: string) =>
    hole<Verwendung[]>(
      '/api/stammdaten/' + encodeURIComponent(objektart) + '/' + encodeURIComponent(schluessel) + '/verwendung',
    ),

  /**
   * Was beim Löschen gilt — **bevor der Anwender einen Weg wählt**
   * (R509, R510).
   *
   * **Drei Arten von Fund, drei Bedeutungen:** Bewegungsdaten stehen auf dem
   * Element; Kinder sind eigene Objekte, die es weiter gibt; eigene
   * Zuordnungen haben ohne das Element keinen Gegenstand und gehen mit.
   * Gesperrt wird nur durch einen freigegebenen Abschluss (R512).
   */
  loeschlage: (objektart: string, schluessel: string) =>
    hole<Loeschlage>(
      '/api/stammdaten/' + encodeURIComponent(objektart) + '/' + encodeURIComponent(schluessel) +
        '/loeschlage',
    ),

  /**
   * Löscht ein Stammdatum — **stilllegen oder restlos entfernen** (R510).
   *
   * **Der Anwender wählt den Weg**, nicht der Aufrufer und nicht der Bestand:
   * *Stilllegen* wahrt die Vergangenheit, *Entfernen* verzichtet ausdrücklich
   * darauf. Wo Kinder hängen, ist das Ziel Pflicht — auf beiden Wegen.
   */
  aussondern: (objektart: string, schluessel: string, auftrag: Loeschauftrag) =>
    hole<Aussonderung>(
      '/api/stammdaten/' + encodeURIComponent(objektart) + '/' + encodeURIComponent(schluessel) +
        '/aussondern',
      { method: 'POST', body: JSON.stringify(auftrag) },
    ),

  /* ---- Datenimport (5.1, R296–R301) ----------------------------------- */

  /** Das Verzeichnis der Datenquellen — auch die, aus denen heute nicht gelesen wird. */
  datenquellen: () => hole<Datenquelle[]>('/api/datenimport/quellen'),

  /** Die Zielfelder: die gelieferte Faktenzeile, aus dem Modell (R522). */
  zielfelder: () => hole<Zielfeld[]>('/api/datenimport/zielfelder'),

  /** Schreibt die Spaltenzuordnung einer Quelle als neue Fassung (R521, R314). */
  abbildungSpeichern: (schluessel: string, abbildungJson: string) =>
    hole<Datenquelle>(`/api/datenimport/quellen/${encodeURIComponent(schluessel)}/abbildung`, {
      method: 'POST',
      body: JSON.stringify({ abbildungJson }),
    }),

  /**
   * Die ersten Zeilen einer Datei — ohne Spaltenzuordnung, für das Blatt.
   *
   * **Die Datei wird dabei nicht archiviert:** Eine Probe ist keine Lieferung.
   */
  dateiprobe: (felder: FormData) =>
    hole<Dateiprobe>('/api/datenimport/probe', { method: 'POST', body: felder }),

  /** Die Lieferungen, jüngste zuerst. */
  importlaeufe: (hoechstens = 25) =>
    hole<Importlaufkopf[]>(`/api/datenimport/laeufe?hoechstens=${hoechstens}`),

  /**
   * Die gespeicherten Datenimporte — was gilt, nicht was geschah (R633).
   *
   * Jede Zeile trägt die Abschlüsse, die über ihr stehen: „geschlossen" sperrt
   * das Löschen und Ersetzen (R635), „freigegeben" ist der Satz davor.
   */
  datenimporte: (periode?: number | null, einheit?: string | null) => {
    const felder = new URLSearchParams()
    if (periode != null) {
      felder.set('periode', String(periode))
    }
    if (einheit) {
      felder.set('einheit', einheit)
    }
    const frage = felder.toString()
    return hole<Datenimportstand[]>(`/api/datenimport/staende${frage ? `?${frage}` : ''}`)
  },

  /**
   * Der Verlauf genau dieses Datenimports — seine eigene Kette, nicht alles,
   * was in dieselbe Gesellschaft, Bewertungsebene und Periode geliefert wurde
   * (5.1 Abschnitt 3.59, R1240).
   */
  verlaufZuBeleg: (beleg: number) =>
    hole<Importlaufkopf[]>(`/api/datenimport/staende/${beleg}/verlauf`),

  /** Was beim Löschen verschwindet — vor der Handlung, nicht danach (R638). */
  datenimportLoeschvorschau: (beleg: number) =>
    hole<Datenimportloeschvorschau>(`/api/datenimport/staende/${beleg}/loeschvorschau`),

  /**
   * Löscht einen gespeicherten Datenimport — **wirklich** (R634).
   *
   * Beleg und Buchungen verschwinden; der Lauf bleibt mit dem Vermerk
   * „gelöscht" und die Rohdatei unberührt (R636).
   */
  datenimportLoeschen: (beleg: number) =>
    hole<null>(`/api/datenimport/staende/${beleg}/loeschen`, { method: 'POST' }),

  /**
   * Prüft eine Lieferung, ohne etwas zu übernehmen.
   *
   * Die Datei geht als Formulardaten und nicht als JSON: Kodiert man sie erst,
   * ist die archivierte Rohdatei nicht mehr die gelieferte (A9).
   */
  importvorschau: (felder: FormData) =>
    hole<Importvorschau>('/api/datenimport/vorschau', { method: 'POST', body: felder }),

  /**
   * Die Periode, die eine Datei nach ihrer Datenquelle nennt — gelesen, bevor
   * geprüft wird (5.1 Abschnitt 3.42, R937).
   *
   * **Gelesen wird im Rechenkern und nicht hier:** Er liest dieselbe Stelle
   * gleich noch einmal beim Prüfen, und zwei Lesarten wären zwei Wahrheiten.
   */
  periodeDerLieferung: (quelle: string, dateiname: string, aktualisiert: number | null) =>
    hole<Quellperiode>(
      `/api/datenimport/periode?quelle=${encodeURIComponent(quelle)}&dateiname=${encodeURIComponent(dateiname)}` +
        (aktualisiert === null ? '' : `&aktualisiert=${aktualisiert}`),
    ),

  /**
   * Der Name eines neuen Datenimports, sobald Gesellschaft und Periode
   * feststehen (5.1 Abschnitt 3.46, R988) — vom Rechenkern, wie nach der
   * Prüfung auch (R374): Eine zweite Formulierung hier liefe auseinander.
   */
  namensvorschlag: (einheit: string, periode: number, wirkung: 'periode' | 'nachbuchung') =>
    hole<{ name: string }>(
      `/api/datenimport/namensvorschlag?${new URLSearchParams({ einheit, periode: String(periode), wirkung })}`,
    ),

  /**
   * Liest einen Stapel Dateien einer Gesellschaft, ohne etwas zu übernehmen
   * (5.1 Abschnitt 3.22, R729).
   *
   * Jede Datei bringt ihre Periode mit — aus dem Dateinamen oder aus einer
   * festen Zelle, je nachdem, was an der Quelle steht (R728).
   */
  /**
   * Liest eine Package-Mappe mit allen Blättern, ohne etwas zu buchen
   * (5.1 Abschnitt 3.26, R743).
   *
   * Die Periode steht im Deckblatt der Mappe; die Gesellschaft wird beim
   * Starten gewählt und mit dem Deckblatt verglichen.
   */
  packagevorschau: (felder: FormData) =>
    hole<Packagevorschau>('/api/datenimport/package/vorschau', { method: 'POST', body: felder }),

  /**
   * Bucht die geprüfte Mappe — alles oder nichts (R743).
   *
   * **Die Mappe geht nicht zweimal über den Bildschirm** (R749): Sie liegt
   * seit der Prüfung im Archiv, und hier reist nur ihre Nummer. Gelesen wird
   * sie trotzdem neu, Blatt für Blatt — das vorige verändert den Stand, gegen
   * den das nächste rechnet.
   */
  packageUebernehmen: (rohdatei: number, einheit: string, quelle: string | null, name: string | null = null) =>
    hole<Packagebericht>('/api/datenimport/package/uebernehmen', {
      method: 'POST',
      body: JSON.stringify({ rohdatei, einheit, quelle, name }),
    }),

  /** Was der Fragebogen der Package-Vorlagen zum Anfangen braucht (5.1 Abschnitt 3.37). */
  packagevorlagengrundlagen: (periode: number) =>
    hole<Packagevorlagengrundlagen>(`/api/datenimport/packagevorlage/grundlagen?periode=${periode}`),

  /** Was die Lieferer in die Hand bekämen — erzeugt und gespeichert wird nichts (R895, R897). */
  packagevorlageVorschau: (auftrag: Packagevorlagenauftrag) =>
    hole<Packagevorlagenvorschau>('/api/datenimport/packagevorlage/vorschau', {
      method: 'POST',
      body: JSON.stringify(auftrag),
    }),

  /**
   * Erzeugt die Mappen und gibt sie zurück — gespeichert wird im Bildschirm,
   * dort, wo der Anwender den Ort wählt (R892, R895).
   *
   * **Nicht über `hole`**, weil die Antwort eine Datei ist und kein JSON. Ein
   * Befund kommt trotzdem als Befund an — mit Satz und Handreichung; jede
   * andere Antwort als `Antwortfehler` mit ihrer Nummer, damit der Bildschirm
   * sagen kann, was sie bedeutet.
   */
  packagevorlageHolen: async (auftrag: Packagevorlagenauftrag): Promise<{ name: string; inhalt: Blob }> => {
    let antwort: Response
    try {
      antwort = await rufe('/api/datenimport/packagevorlage', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Coko-Sprache': aktuelleSprache() },
        body: JSON.stringify(auftrag),
      })
    } catch {
      throw gemeldet(new Betriebsstoerung('meldung.stoerung.unerreichbar'))
    }

    if (antwort.status === 422) {
      const abbruch = await antwort.json()
      throw new Rechenabbruch(abbruch.meldung, abbruch.handreichung ?? null)
    }

    if (antwort.status === 401) {
      window.dispatchEvent(new CustomEvent(ABGEMELDET))
      throw new Nichtangemeldet()
    }

    // Eine Störung des Dienstes gehört ins Störungsfenster (7.2 Abschnitt 30, R1003).
    if (!antwort.ok) {
      throw await stoerungAus(antwort)
    }

    const dateiname =
      /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(antwort.headers.get('Content-Disposition') ?? '')?.[1] ??
      'Reporting-Package.xlsx'
    return { name: decodeURIComponent(dateiname), inhalt: await antwort.blob() }
  },

  stapelvorschau: (felder: FormData) =>
    hole<Stapelvorschau>('/api/datenimport/stapel/vorschau', { method: 'POST', body: felder }),

  /**
   * Bucht die geprüften Posten, nach Perioden aufsteigend (R730).
   *
   * Beim ersten Befund hält der Stapel an; was davor liegt, bleibt gebucht.
   * Der Bericht sagt, wo er stehen geblieben ist.
   */
  stapelUebernehmen: (
    posten: { vorschau: Importvorschau; ersetzt: number | null; name: string | null }[],
    quelle: string | null,
    vorlage: string | null = null,
    /** Die Antworten auf die Vorschläge der Datenquelle — einmal für den Stapel (R873). */
    antworten: { konto: string; richtung: Vorschlagsrichtung; angenommen: boolean }[] = [],
  ) =>
    hole<Stapelbericht>('/api/datenimport/stapel/uebernehmen', {
      method: 'POST',
      body: JSON.stringify({ posten, quelle, vorlage, antworten }),
    }),

  /**
   * Liest die aufbewahrte Datei eines Datenimports noch einmal, **ohne etwas
   * zu übernehmen** (5.1 Abschnitt 3.39, R931).
   *
   * Gelesen wird die Lieferung, nicht die Buchung: Die Verkehrszahl entsteht
   * neu gegen den Stand von jetzt. Einen Eintritt in den Konsolidierungskreis
   * kennt der Import nicht (R1591).
   */
  datenimportAusAufbewahrung: (
    beleg: number,
    angaben: {
      wirkung?: 'periode' | 'nachbuchung'
      berichtsperiode?: number | null
    } = {},
  ) => {
    const frage = new URLSearchParams()
    if (angaben.wirkung) {
      frage.set('wirkung', angaben.wirkung)
    }
    if (angaben.berichtsperiode) {
      frage.set('berichtsperiode', String(angaben.berichtsperiode))
    }
    const anhang = frage.toString()
    return hole<Importvorschau>(`/api/datenimport/staende/${beleg}/vorschau${anhang ? `?${anhang}` : ''}`, {
      method: 'POST',
    })
  },

  /** Liest die aufbewahrten Dateien aller angemahnten Datenimporte, ohne zu buchen (R932). */
  aktualisierungsvorschau: () =>
    hole<Aktualisierungsvorschau>('/api/datenimport/aktualisierung/vorschau', { method: 'POST' }),

  /**
   * Bucht die angemahnten Datenimporte aus ihren aufbewahrten Dateien — je
   * Gesellschaft und Datenquelle aufsteigend nach Periode (R932). Die Antworten
   * auf die Vorschläge gelten je Datenquelle einmal für den Zug (R873).
   */
  aktualisierungUebernehmen: (
    antworten: {
      quelle: string
      antworten: { konto: string; richtung: Vorschlagsrichtung; angenommen: boolean }[]
    }[] = [],
  ) =>
    hole<Stapelbericht>('/api/datenimport/aktualisierung/uebernehmen', {
      method: 'POST',
      body: JSON.stringify({ antworten }),
    }),


  /**
   * Prüft eine Stammdatenmappe, **ohne etwas zu übernehmen** (5.1 Abschnitt 10,
   * R560–R566).
   *
   * Dieselbe Trennung wie beim Import der Bewegungsdaten: Was das System
   * vorbereitet hat, wird erst durch eine Entscheidung wirksam (R5).
   */
  stammdatenVorschau: (felder: FormData) =>
    hole<Austauschvorschau>('/api/stammdaten/austausch/vorschau', { method: 'POST', body: felder }),

  /**
   * Übernimmt die geprüfte Mappe — **alles oder nichts** (R563).
   *
   * Ohne `rechnendBestaetigt` weist der Dienst ab, sobald eine Zeile eine
   * rechnende Eigenschaft ändert; übernommen wird dann **gar nichts**, nicht
   * „der Rest".
   */
  stammdatenUebernehmen: (felder: FormData) =>
    hole<Austauschergebnis>('/api/stammdaten/austausch/uebernahme', { method: 'POST', body: felder }),

  /** Die Zuordnung lokaler Konten je Gesellschaft (B8). */
  kontenmapping: (einheit?: string) =>
    hole<Kontenmapping[]>(
      '/api/datenimport/mapping' + (einheit ? '?einheit=' + encodeURIComponent(einheit) : ''),
    ),

  /**
   * Ordnet ein lokales Konto zu — als neue Fassung, gültig ab einer Periode.
   *
   * Kein Vorschlag wird ohne diesen Ruf wirksam (R5, R138): Auch bei
   * hundertprozentiger Übereinstimmung bestätigt ein Mensch.
   */
  zuordnen: (
    einheit: string,
    lokalnummer: string,
    lokalbezeichnung: string | null,
    konto: string,
    gueltigVon: number,
    /**
     * Der Partner (R750, R1172) — leer räumt ihn ab, ohne Angabe bleibt er —
     * und der ausdrücklich eingetragene Monat (R1171, R1173).
     */
    weiteres: { icPartner?: string; wirksamAb?: number } = {},
  ) =>
    hole<Kontenmapping>('/api/datenimport/mapping', {
      method: 'POST',
      body: JSON.stringify({
        einheit,
        lokalnummer,
        lokalbezeichnung,
        konto,
        gueltigVon,
        icPartner: weiteres.icPartner,
        wirksamAb: weiteres.wirksamAb,
      }),
    }),

  /**
   * Legt eine Zuordnung still (R305).
   *
   * **Nicht löschen:** Angefügt wird eine Fassung, die sagt „gilt nicht mehr".
   * Ein Abschluss auf altem Stand findet die Zuordnung von damals weiterhin.
   */
  /** Ab einem Monat (R1160) — ohne Monat für keinen mehr. */
  zuordnungStilllegen: (schluessel: string, ab?: number) =>
    hole<void>(
      `/api/datenimport/mapping/${encodeURIComponent(schluessel)}/stilllegen` + (ab ? `?ab=${ab}` : ''),
      { method: 'POST' },
    ),

  /** Nimmt das Ende einer stillgelegten Zuordnung zurück (R1163). */
  zuordnungInKraftSetzen: (schluessel: string) =>
    hole<void>(`/api/datenimport/mapping/${encodeURIComponent(schluessel)}/inkraft`, { method: 'POST' }),

  /**
   * Jeder Handgriff, der ein Recht verlangt, mit diesem Recht — vom Server aus
   * seinen Endpunkten gelesen (8.1 Abschnitt 7, R1181).
   */
  handgriffe: () => hole<Handgriff[]>('/api/rechte/handgriffe'),

  /** Der Verlauf einer Zuordnung — jede Fassung aller Zeiträume, neueste oben (R1175). */
  zuordnungVerlauf: (sorte: 'konto' | 'bewegungsart' | 'wert', schluessel: string) =>
    hole<Verlaufseintrag[]>(
      '/api/datenimport/zuordnungen/verlauf?sorte=' + sorte + '&schluessel=' + encodeURIComponent(schluessel),
    ),

  /** Welche Gesellschaften eine Zuordnung verwenden — für die Rückfrage (R1161). */
  zuordnungVerwender: (sorte: 'konto' | 'bewegungsart' | 'wert', schluessel: string) =>
    hole<string[]>(
      '/api/datenimport/zuordnungen/verwender?sorte=' + sorte + '&schluessel=' + encodeURIComponent(schluessel),
    ),

  /* ---- Die Bewegungsart (5.1 Abschnitt 3.12, R593–R596) ---------------- */

  /** Die Zuordnung gelieferter Bewegungsartcodes (R593). */
  bewegungsartmapping: (einheit?: string) =>
    hole<Bewegungsartmapping[]>(
      '/api/datenimport/bewegungsartmapping' +
        (einheit ? '?einheit=' + encodeURIComponent(einheit) : ''),
    ),

  /**
   * Der Vorrat, aus dem beim Anlegen gewählt wird (R594).
   *
   * **Nur Kerne.** Gewählt wird die Bedeutung, nicht ihre Merkmale: Spiegel,
   * KFR-Wirkung, Zahlungswirksamkeit und Kurstyp erbt das Neue (R31).
   */
  kernbewegungsarten: () => hole<Kernbewegungsart[]>('/api/datenimport/kernbewegungsarten'),

  /**
   * Ordnet einen gelieferten Bewegungsartcode zu (R593).
   *
   * **Bis zum 2026-09-18 ging das nirgends:** Ein Code, der auf keine
   * Bewegungsart zeigte, war ein Befund ohne Ausweg.
   */
  bewegungsartZuordnen: (
    einheit: string,
    code: string,
    bezeichnung: string | null,
    bewegungsart: string,
    gueltigVon: number,
    /** Der ausdrücklich eingetragene Monat (R1171, R1173). */
    wirksamAb?: number,
  ) =>
    hole<Bewegungsartmapping>('/api/datenimport/bewegungsartmapping', {
      method: 'POST',
      body: JSON.stringify({ einheit, code, bezeichnung, bewegungsart, gueltigVon, wirksamAb }),
    }),

  /** Erzeugt aus einem gelieferten Code eine Bewegungsart unter ihrem Kern (R594). */
  bewegungsartErzeugen: (
    einheit: string,
    code: string,
    bezeichnung: string | null,
    kern: string,
    gueltigVon: number,
    wirksamAb?: number,
  ) =>
    hole<Bewegungsartanlage>('/api/datenimport/bewegungsart', {
      method: 'POST',
      body: JSON.stringify({ einheit, code, bezeichnung, kern, gueltigVon, wirksamAb }),
    }),

  /* ---- Die übrigen Vorratsfelder (5.1 Abschnitt 3.13, R597–R600) ------- */

  /** Die Zuordnung gelieferter Werte je Zielfeld (R597) — ohne Feld alle. */
  wertzuordnungen: (feld?: string) =>
    hole<Wertzuordnung[]>(
      '/api/datenimport/wertzuordnung' + (feld ? '?feld=' + encodeURIComponent(feld) : ''),
    ),

  /**
   * Ordnet einen gelieferten Wert zu (R597).
   *
   * **Das Feld gehört in den Auftrag, nicht in den Pfad:** Sonst wären es elf
   * Rufe, und der zwölfte käme mit der nächsten Analysedimension.
   */
  wertZuordnen: (
    einheit: string,
    feld: string,
    code: string,
    bezeichnung: string | null,
    ziel: string,
    gueltigVon: number,
    wirksamAb?: number,
  ) =>
    hole<Wertzuordnung>('/api/datenimport/wertzuordnung', {
      method: 'POST',
      body: JSON.stringify({ einheit, feld, code, bezeichnung, ziel, gueltigVon, wirksamAb }),
    }),

  /** Legt aus einem gelieferten Wert einen Vorratswert an (R599). */
  wertErzeugen: (
    einheit: string,
    feld: string,
    code: string,
    bezeichnung: string | null,
    gueltigVon: number,
    wirksamAb?: number,
  ) =>
    hole<Wertanlage>('/api/datenimport/wert', {
      method: 'POST',
      body: JSON.stringify({ einheit, feld, code, bezeichnung, gueltigVon, wirksamAb }),
    }),

  /** Legt eine Wertzuordnung still (R305). */
  wertzuordnungStilllegen: (schluessel: string, ab?: number) =>
    hole<void>(
      `/api/datenimport/wertzuordnung/${encodeURIComponent(schluessel)}/stilllegen` + (ab ? `?ab=${ab}` : ''),
      { method: 'POST' },
    ),

  wertzuordnungInKraftSetzen: (schluessel: string) =>
    hole<void>(`/api/datenimport/wertzuordnung/${encodeURIComponent(schluessel)}/inkraft`, { method: 'POST' }),

  /** Legt eine Bewegungsartzuordnung still (R305). */
  bewegungsartzuordnungStilllegen: (schluessel: string, ab?: number) =>
    hole<void>(
      `/api/datenimport/bewegungsartmapping/${encodeURIComponent(schluessel)}/stilllegen` +
        (ab ? `?ab=${ab}` : ''),
      { method: 'POST' },
    ),

  bewegungsartzuordnungInKraftSetzen: (schluessel: string) =>
    hole<void>(`/api/datenimport/bewegungsartmapping/${encodeURIComponent(schluessel)}/inkraft`, {
      method: 'POST',
    }),

  /**
   * Woraus jede Gruppe von Bewertungsebenen besteht (2.4 Abschnitt 9, R398).
   *
   * Das Eigenschaftsblatt sagt, **was** eine Bewertungsebene ist. Der Baum
   * sagt, **woraus** eine Gruppe besteht — und das sagt kein Blatt, weil es an
   * mehreren Zeilen zugleich hängt.
   */
  ledgeraufbau: () => hole<Aufbaubild>('/api/stammdaten/ledgeraufbau'),

  /** Legt eine Schicht oder Gruppe an und hängt sie ein (R396). */
  schichtAnlegen: (auftrag: {
    gruppe: string | null
    name: string
    art: 'rohdaten' | 'delta' | 'gruppe'
    /** Nur an der Wurzel zu sagen — darunter erbt sie von der Gruppe (R702). */
    rechnungslegung?: string | null
  }) =>
    hole<{ schluessel: string }>('/api/ledgeraufbau/anlegen', {
      method: 'POST',
      body: JSON.stringify({ rechnungslegung: null, ...auftrag }),
    }),

  /** Nimmt eine vorhandene Schicht zusätzlich in eine Gruppe (R399). */
  schichtAufnehmen: (gruppe: string, mitglied: string) =>
    hole<{ aufgenommen: boolean }>('/api/ledgeraufbau/aufnehmen', {
      method: 'POST',
      body: JSON.stringify({ gruppe, mitglied }),
    }),

  /** Nimmt ein Mitglied aus einer Gruppe — die Schicht selbst bleibt. */
  schichtAushaengen: (gruppe: string, mitglied: string) =>
    hole<{ ausgehaengt: boolean }>('/api/ledgeraufbau/aushaengen', {
      method: 'POST',
      body: JSON.stringify({ gruppe, mitglied }),
    }),

  /** Schiebt ein Mitglied eine Stelle auf oder ab (R392). */
  schichtVerschieben: (gruppe: string, mitglied: string, hoch: boolean) =>
    hole<{ verschoben: boolean }>('/api/ledgeraufbau/verschieben', {
      method: 'POST',
      body: JSON.stringify({ gruppe, mitglied, hoch }),
    }),

  /** Benennt eine Bewertungsebene um — im Baum, wo man sie sieht (R317). */
  schichtBenennen: (ledger: string, sprache: string, name: string) =>
    hole<{ benannt: boolean }>('/api/ledgeraufbau/benennen', {
      method: 'POST',
      body: JSON.stringify({ ledger, sprache, name }),
    }),

  /**
   * Bilanz und GuV als Struktur — ohne Lauf, aus den Stammdaten (R307).
   *
   * **Ohne `plan` antwortet der Dienst mit dem Vorgabeplan** der Ebene. Das ist
   * richtig, wo ein Bildschirm nur *einen* Plan braucht — beim Zuordnen etwa —,
   * und es war bis zum 2026-09-18 der einzige Weg: Ein zweiter Plan derselben
   * Ebene war damit unerreichbar, obwohl der Dienst ihn seit jeher liefert
   * (R614).
   *
   * **`null`, wenn die Ebene keinen Positionsplan führt** — eine Auskunft,
   * kein Fehler (7.2 Abschnitt 39, R1210). Ein Fehler heißt: Der Plan kam nicht.
   */
  gliederung: (ledger: string, periode: number, plan?: string) =>
    hole<Gliederungsbild | null>(
      '/api/datenimport/gliederung?ledger=' +
        encodeURIComponent(ledger) +
        '&periode=' +
        periode +
        (plan ? '&plan=' + encodeURIComponent(plan) : ''),
    ),

  /**
   * Wo jedes Konzernkonto steht — in jeder Gliederung, zum Monat (5.1
   * Abschnitt 3.68, R1853). Die Spalte *Position* der Zuordnungen; die
   * Excel-Mappe liest dieselbe Abfrage.
   */
  kontopositionen: (periode: number) =>
    hole<Kontoposition[]>('/api/datenimport/zuordnungen/positionen?periode=' + periode),

  /**
   * Hängt eine Position unter eine andere — oder zwischen zwei Geschwister
   * (R319).
   *
   * **`korrektur` entscheidet, was der Zug bedeutet:** `false` schneidet die
   * Zeitscheibe (die alte Zeile endet, eine neue beginnt), `true` schreibt
   * dieselbe Zeile in neuer Fassung. Mit `immer` jede Zuordnung der Position
   * im Plan, über die ganze Zeitachse — wie beim Konto (R1697, R860).
   */
  positionUmhaengen: (
    position: string,
    eltern: string | null,
    vorPosition: string | null,
    abPeriode: number,
    korrektur: boolean,
    immer = false,
  ) =>
    hole<Gliederungszug>('/api/gliederung/position', {
      method: 'POST',
      body: JSON.stringify({ position, eltern, vorPosition, abPeriode, korrektur, immer }),
    }),

  /**
   * Hängt ein Konto unter eine andere Position (R320) — mit `immer` jede
   * Zuordnung des Kontos im Plan, über die ganze Zeitachse (R860).
   */
  /** Die Kontengruppe eines Kontos ändern — ab einem Monat oder immer (R1648, R1649). */
  kontogruppeSetzen: (konto: string, gruppe: string, abPeriode: number, immer: boolean) =>
    hole<number>('/api/gliederung/konto/gruppe', {
      method: 'POST',
      body: JSON.stringify({ konto, gruppe, abPeriode, immer }),
    }),

  kontoUmhaengen: (konto: string, position: string, abPeriode: number, korrektur: boolean, immer = false) =>
    hole<Gliederungszug>('/api/gliederung/konto', {
      method: 'POST',
      body: JSON.stringify({ konto, position, abPeriode, korrektur, immer }),
    }),

  /** Der Haken „Ausweis der Stichtagskursanpassung auf separatem Konto“ an einem Konto (R1655–R1659). */
  stichtagskursanpassung: (konto: string) =>
    hole<Stichtagskursanpassungsstand>(`/api/konten/${encodeURIComponent(konto)}/stichtagskursanpassung`),

  /** Setzt oder nimmt den Haken ab einem Monat (R1657); beim ersten Setzen entsteht das eigene Konto (R1656). */
  stichtagskursanpassungSetzen: (konto: string, getrennt: boolean, ab: number) =>
    hole<Stichtagskursanpassungsstand>(`/api/konten/${encodeURIComponent(konto)}/stichtagskursanpassung`, {
      method: 'POST',
      body: JSON.stringify({ getrennt, ab }),
    }),

  /** Nimmt ein Konto von einer Position — ab einer Periode oder immer (R859, R860). */
  kontoAushaengen: (konto: string, position: string, abPeriode: number, immer: boolean) =>
    hole<number>('/api/gliederung/konto/aushaengen', {
      method: 'POST',
      body: JSON.stringify({ konto, position, abPeriode, immer }),
    }),

  /**
   * Legt eine Position an der Stelle an, an die sie gehört (R321).
   *
   * **Ohne Code** (R676, R678, berichtigt am 2026-09-22): Der Dienst nimmt
   * seit dem 2026-09-21 nur noch die Bezeichnung entgegen — `Einfuegeauftrag`
   * hat kein Codefeld mehr, und der Schlüssel wird gezogen. Hier stand er
   * trotzdem noch im Aufruf und wurde mitgeschickt; die Maske darüber fragte
   * den Anwender danach, und der Dienst warf ihn weg.
   */
  positionEinfuegen: (
    plan: string,
    name: string | null,
    eltern: string | null,
    vorPosition: string | null,
    abPeriode: number,
  ) =>
    hole<Gliederungszug>('/api/gliederung/einfuegen', {
      method: 'POST',
      body: JSON.stringify({ plan, name, eltern, vorPosition, abPeriode }),
    }),

  /**
   * Was unter dieser Position entstehen kann — und was es erben würde
   * (7.4 Abschnitt 14, R495–R497).
   *
   * **Zwei Auskünfte in einer:** *was* hier angelegt werden darf, und *was*
   * das Neue von seiner Stelle erbt. Die Maske fragt sie, bevor sie etwas
   * anbietet — ein Menüpunkt, der beim Speichern abgewiesen wird, ist ein
   * Versprechen, das beim Anklicken bricht.
   */
  anlagevorschau: (position: string, periode: number) =>
    hole<Anlagevorschau>(
      '/api/gliederung/anlagevorschau?position=' +
        encodeURIComponent(position) +
        '&periode=' +
        periode,
    ),

  /** Legt ein Konzernkonto unter einer Position an (R494, R308). */
  /** Die Bezeichnung trägt die Nummer vorn (R678) — ein eigenes Codefeld gibt es nicht mehr (R676). */
  kontoAnlegen: (
    position: string,
    bezeichnung: string | null,
    gruppe: string | null,
    abPeriode: number,
  ) =>
    hole<Kontoeinfuegung>('/api/gliederung/konto-anlegen', {
      method: 'POST',
      body: JSON.stringify({ position, bezeichnung, gruppe, abPeriode }),
    }),

  /**
   * Was unter dieser Zeile der Kapitalflussrechnung entstehen kann (R507).
   *
   * **Dieselbe Gestalt wie die Vorschau am Positionsplan** — die Maske ist ein
   * Bauteil (R317). Was hier keine Entsprechung hat, kommt leer: Eine
   * KFR-Zeile trägt keine Konten und keinen Gültigkeitszeitraum.
   */
  kfrAnlagevorschau: (zeile: string, periode: number, art?: 'unter' | 'abschnitt') =>
    hole<Anlagevorschau>(
      '/api/kfr/anlagevorschau?zeile=' +
        encodeURIComponent(zeile) +
        '&periode=' +
        periode +
        (art ? '&art=' + art : ''),
    ),

  /**
   * Legt eine Zeile an — und die Summe rechnet sie mit (R507). **Mit Art und
   * Filter** der Rechtsklick an jeder Zeile (2.8 Abschnitt 13.36, R1124):
   * Unterzeile oder Zeile danach, samt der Regel, die in sie führt.
   */
  kfrZeileAnlegen: (
    eltern: string,
    code: string,
    name: string | null,
    /**
     * Wo die Position entsteht — unter einer Position oder unter dem Band
     * eines Abschnitts (2.1 Abschnitt 3.6, R1706) —, ihre Referenzen, oder
     * statt einer Position eine Summenzeile mit ihrer Zählweise (R1358).
     */
    filter?: {
      art: 'unter' | 'abschnitt'
      referenzen: { quelle: string; bewegungsarten: string[]; umkehren: boolean }[]
      zaehlweise: string | null
    },
  ) =>
    hole<Kfreinfuegung>('/api/kfr/zeile-anlegen', {
      method: 'POST',
      body: JSON.stringify({ eltern, code, name, ...filter }),
    }),

  /**
   * **Neues Konto …** an einer Position der Kapitalflussrechnung: ein
   * Statistikkonto samt der Zuordnung, mit der es unter ihr steht (2.1
   * Abschnitt 3.6, R1715, R1727).
   */
  statistikkontoAnlegen: (zeile: string, bezeichnung: string) =>
    hole<Statistikkontoeinfuegung>('/api/kfr/statistikkonto-anlegen', {
      method: 'POST',
      body: JSON.stringify({ zeile, bezeichnung }),
    }),

  /** Was das Löschen einer Zeile mitnimmt, oder warum es nicht geht (2.8 Abschnitt 13.41, R1334, R1335). */
  kfrLoeschlage: (zeile: string) =>
    hole<Kfrloeschlage>('/api/kfr/loeschlage?zeile=' + encodeURIComponent(zeile)),

  /** Die Ebene eines Elements in ihrer Reihenfolge (7.4 Abschnitt 32, R1341). */
  reihenfolge: (art: string, element: string) =>
    hole<Reihenfolge>('/api/reihenfolge?art=' + encodeURIComponent(art) + '&element=' + encodeURIComponent(element)),

  /** Schreibt die Reihenfolge einer Ebene und ob sie sortiert bleibt (R1342, R1343). */
  reihenfolgeSetzen: (auftrag: { art: string; element: string; folge: string[]; immerSortiert: boolean }) =>
    hole<Reihenfolge>('/api/reihenfolge', {
      method: 'POST',
      body: JSON.stringify(auftrag),
    }),

  /** Löscht eine Zeile — sie verlässt ihre Summe, ihre Referenzen gehen mit (R1334). */
  kfrZeileLoeschen: (zeile: string) =>
    hole<Kfrloeschlage>('/api/kfr/zeile-loeschen', {
      method: 'POST',
      body: JSON.stringify({ zeile }),
    }),

  /** Legt eine Unterposition unter einer Berichtszeile an (R494, R321). */
  /** Die Bezeichnung trägt die Nummer vorn (R678, R676). */
  unterpositionAnlegen: (eltern: string, name: string | null, abPeriode: number) =>
    hole<Gliederungszug>('/api/gliederung/unterposition', {
      method: 'POST',
      body: JSON.stringify({ eltern, name, abPeriode }),
    }),

  /**
   * Erzeugt aus einem lokalen Konto ein neues Konzernkonto (R308).
   *
   * Drei Anfügungen in einem Handgriff: Konto, Beschriftung, Stelle im
   * Positionsplan — dazu die Zuordnung des lokalen Kontos darauf.
   */
  kontoErzeugen: (
    einheit: string,
    lokalnummer: string,
    lokalbezeichnung: string | null,
    position: string,
    gruppe: string | null,
    gueltigVon: number,
    wirksamAb?: number,
  ) =>
    hole<Kontoanlage>('/api/datenimport/konto', {
      method: 'POST',
      body: JSON.stringify({ einheit, lokalnummer, lokalbezeichnung, position, gruppe, gueltigVon, wirksamAb }),
    }),

  /**
   * Die Zuordnungen, die die geltenden Lieferungen einer Gesellschaft
   * verwenden (5.1 Abschnitt 3.49, R1153) — je Sorte die Schlüssel.
   *
   * **Die Mappe läuft nicht hierüber**, sondern als Verweis auf
   * `/api/datenimport/zuordnungen/export` (R1155) — eine Datei, die der
   * Browser herunterlädt, soll er auch selbst holen.
   */
  verwendeteZuordnungen: (einheit: string) =>
    hole<VerwendeteZuordnungen>(
      '/api/datenimport/zuordnungen/verwendet?einheit=' + encodeURIComponent(einheit),
    ),

  /** Übernimmt die geprüfte Vorschau in das Journal (R132). */
  /**
   * Übernimmt die geprüften Zeilen — und ersetzt dabei **genau das**, was
   * `ersetzt` nennt (R639).
   *
   * `null` heißt: Es wird nichts ersetzt, der Datenimport kommt hinzu. Bis zum
   * 2026-09-20 suchte der Rechenkern selbst nach dem Vorgänger und traf jeden
   * Lieferbeleg derselben Gesellschaft, Ebene und Periode — auch eine
   * Nachbuchung des Vormonats, die dort nur zufällig lag.
   */
  importUebernehmen: (
    vorschau: Importvorschau,
    quelle: string | null,
    vorlage: string | null,
    ersetzt: number | null = null,
    /** Worunter der Anwender diesen Datenimport wiederfindet (R374). */
    name: string | null = null,
  ) =>
    hole<Importlaufkopf>('/api/datenimport/uebernehmen', {
      method: 'POST',
      body: JSON.stringify({ vorschau, quelle, vorlage, ersetzt, name }),
    }),

  /**
   * Pflegt ein Stammdatum — als neue Fassung, nie als Änderung (R241).
   *
   * Die Antwort sagt, was wirklich geschrieben wurde: Ein Auftrag, der nichts
   * ändert, schreibt keine Fassung. Sonst entstünde bei jedem Klick auf
   * „Speichern" eine Zeile in der Fassungsreihe, und die Reihe wäre nach einer
   * Woche unlesbar.
   */
  /**
   * Schreibt den Satz des Hauses dazu, **wofür** ein Feld gepflegt wird (R697).
   *
   * **Er gilt für die Objektart und nicht für ein Objekt.** Wer ihn am Blatt
   * einer Gesellschaft ändert, ändert ihn für alle — deshalb steht der Hinweis
   * am Feld und nicht im Kleingedruckten.
   */
  zweckPflegen: (objektart: string, feld: string, sprache: string, text: string) =>
    hole<{ erfassungNr: number }>(
      `/api/stammdaten/${encodeURIComponent(objektart)}/zweck/${encodeURIComponent(feld)}`,
      { method: 'POST', body: JSON.stringify({ sprache, text }) },
    ),

  stammdatumPflegen: (objektart: string, schluessel: string, auftrag: Pflegeauftrag) =>
    hole<Pflegeergebnis>(
      `/api/stammdaten/${encodeURIComponent(objektart)}/${encodeURIComponent(schluessel)}`,
      { method: 'POST', body: JSON.stringify(auftrag) },
    ),

  /**
   * Zieht ein Stammdatum an eine andere Stelle seiner Reihenfolge (R709).
   *
   * **Der zweite Weg neben dem getippten Zahlenfeld.** Beide schreiben
   * dasselbe Feld — es gibt keine zweite Ordnung daneben. `vor` ist der
   * Schlüssel, vor den es fällt; `null` heißt: ans Ende der Gruppe.
   *
   * **`gruppenfeld` begrenzt die Reihe**, in der gerechnet wird: Bei der
   * Bewegungsart ist das `spiegel`, denn ihre Kategorie *ist* der Spiegel
   * (R688). Damit kann der Startwert, der allen Spiegeln gehört, gar nicht
   * zwischen die Bewegungen eines einzelnen geraten.
   */
  stammdatumEinordnen: (
    objektart: string,
    schluessel: string,
    vor: string | null,
    gruppenfeld: string | null,
  ) =>
    hole<{ geschrieben: number }>(
      `/api/stammdaten/${encodeURIComponent(objektart)}/${encodeURIComponent(schluessel)}/einordnen`,
      { method: 'POST', body: JSON.stringify({ vor, gruppenfeld }) },
    ),

  /**
   * Das Anhangsbuch einer Periode (2.15).
   *
   * **In einem Zug, nicht je Angabe.** Die Antwort trägt alle Angaben, ihre
   * Texte in allen gepflegten Sprachen und die bereits aufgelösten Anker — der
   * einzige Bildschirm, der sonst sechzig kleine Abfragen stellen würde.
   */
  anhangsbuch: (kreis: string, ledger: string, periode: number) => {
    const frage = new URLSearchParams({
      kreis,
      ledger,
      periode: String(periode),
    })
    return hole<Anhangsbuch>(`/api/anhang/buch?${frage}`)
  },

  /** Der Angabenkatalog mit Fundstelle, Art und Herkunft (R219). */
  angabenkatalog: (ausgabe?: number) =>
    hole<Angabenkatalogeintrag[]>(
      `/api/anhang/katalog${ausgabe ? `?ausgabe=${ausgabe}` : ''}`,
    ),

  /* --- Der Konzernabschluss (2.13 Abschnitt 8, R787) ------------------- */

  /** Die Konzernabschluesse, juengstes Geschaeftsjahr zuerst. */
  konzernabschluesse: () => hole<Abschlusszeile[]>('/api/abschluss/konzernabschluesse'),

  /**
   * Legt einen Konzernabschluss an und haengt seine Prozesse ein (R787, R789).
   *
   * `vorlage` leer heisst: die Fassung ab Werk.
   */
  konzernabschlussAnlegen: (
    name: string,
    kreis: string,
    ledger: string,
    vonPeriode: number,
    vorlage: string | null,
  ) =>
    hole<Abschlusszeile[]>('/api/abschluss/konzernabschluesse', {
      method: 'POST',
      body: JSON.stringify({ name, kreis, ledger, vonPeriode, vorlage }),
    }),

  /**
   * Stoesst den Konzernabschluss an -- alle zwoelf Monate (R787).
   *
   * **Ein Monat, der sich nicht rechnen laesst, haelt die uebrigen nicht auf**:
   * Er steht mit seiner Meldung im Bericht.
   *
   * **Die Antwort kommt sofort** (7.2 Abschnitt 40, R1252): Gerechnet wird im
   * Hintergrund; wie weit, sagt `konzernabschlusslaeufe`. Rechnet derselbe
   * schon, kommt der laufende zurück (R1254).
   */
  konzernabschlussAnstossen: (schluessel: string) =>
    hole<Rechenstand>(
      `/api/abschluss/konzernabschluesse/${encodeURIComponent(schluessel)}/rechnen`,
      { method: 'POST' },
    ),
  /** Was gerade rechnet und was in den letzten zehn Minuten endete (R1252). */
  konzernabschlusslaeufe: () => hole<Rechenstand[]>('/api/abschluss/konzernabschluesse/laeufe'),
  /* --- Die Schritte des Konzernabschlusses (2.9 Abschnitt 10, R782) ---- */

  /** Die Folge, der Vorrat der Werkzeuge und was auffaellt -- in einem Ruf. */
  /**
   * `konzernabschluss` leer heisst: die Vorlage ab Werk (R789).
   */
  konsolidierungsschritte: (konzernabschluss?: string | null) =>
    hole<Schrittbild>(
      '/api/abschluss/schritte'
        + (konzernabschluss ? `?konzernabschluss=${encodeURIComponent(konzernabschluss)}` : ''),
    ),

  /**
   * Legt einen Schritt an oder schreibt ihn fort (R3, R782).
   *
   * `schluessel` leer heisst: neu anlegen.
   */
  konsolidierungsschrittPflegen: (
    schluessel: string | null,
    werkzeug: string,
    name: string,
    rang?: number,
    konzernabschluss?: string | null,
  ) =>
    hole<Schrittzeile[]>('/api/abschluss/schritte', {
      method: 'POST',
      body: JSON.stringify({
        schluessel,
        werkzeug,
        name,
        rang: rang ?? null,
        konzernabschluss: konzernabschluss ?? null,
      }),
    }),

  /**
   * Was ein Prozess gebucht hat -- ueber alle Monate seines Konzernabschlusses
   * (R791).
   */
  /**
   * Was ein Prozess gebucht hat -- ein Monat zur Zeit (R815).
   *
   * `periode` leer heisst: der juengste Monat mit Buchungen.
   */
  konsolidierungsschrittNachweis: (schluessel: string, periode?: number | null) =>
    hole<Prozessnachweis>(
      `/api/abschluss/schritte/${encodeURIComponent(schluessel)}/nachweis`
        + (periode ? `?periode=${periode}` : ''),
    ),

  /**
   * Setzt, was ein Prozess am Konzernhandbuch vorbei bucht (R817).
   *
   * **Leer heisst zurueckgenommen**: Ein Auftrag ohne Werte stellt den Prozess
   * wieder unter das Konzernhandbuch.
   */
  konsolidierungsschrittUebersteuern: (
    schluessel: string,
    werte: Record<string, string>,
    konzernabschluss?: string | null,
  ) =>
    hole<Schrittzeile[]>(
      `/api/abschluss/schritte/${encodeURIComponent(schluessel)}/uebersteuerung`,
      {
        method: 'POST',
        body: JSON.stringify({ werte, konzernabschluss: konzernabschluss ?? null }),
      },
    ),

  /**
   * Grenzt einen Prozess ab: Positionen, Differenzenkonto, Toleranz (R800).
   *
   * **Abgegrenzt wird in Positionen**, nicht in Konten -- und eine gewaehlte
   * Position schliesst ihre Unterpositionen ein. Leer heisst: alles, was das
   * Werkzeug findet.
   */
  konsolidierungsschrittAbgrenzen: (
    schluessel: string,
    abgrenzung: {
      positionen: string[]
      differenzkonto: string | null
      toleranz: number | null
      differenzkontoWaehrung: string | null
      /** Die Standardmarge als Anteil am Verrechnungspreis, 0.2 für 20 % (R1561, R1562). */
      standardmarge?: number | null
      /** Die Margen je Lieferbeziehung, Lieferant → Empfänger (R1561). */
      paarmargen?: { lieferant: string; empfaenger: string; marge: number }[] | null
      /** Die Beteiligungs- und Eigenkapitalpositionen der Kapitalkonsolidierung (R1603). */
      beteiligungen?: string[] | null
      eigenkapital?: string[] | null
    },
    konzernabschluss?: string | null,
  ) =>
    hole<Schrittzeile[]>(
      `/api/abschluss/schritte/${encodeURIComponent(schluessel)}/abgrenzung`,
      {
        method: 'POST',
        body: JSON.stringify({ ...abgrenzung, konzernabschluss: konzernabschluss ?? null }),
      },
    ),

  /** Woran ein Prozess rechnet -- bei der Kapitalkonsolidierung seine Beteiligungen (R822). */
  konsolidierungsschrittGrundlage: (schluessel: string) =>
    hole<Prozessgrundlage>(
      `/api/abschluss/schritte/${encodeURIComponent(schluessel)}/grundlage`,
    ),

  /**
   * Setzt, was ein Prozess umgliedert und saldiert (R818).
   *
   * **Nur das Werkzeug ausweiskorrektur nimmt sie an** -- eine Liste an einem
   * anderen Prozess waere eine Zusage, die der Rechenkern nicht haelt.
   */
  konsolidierungsschrittKorrigieren: (
    schluessel: string,
    regeln: Korrekturangabe[],
    konzernabschluss?: string | null,
  ) =>
    hole<Schrittzeile[]>(
      `/api/abschluss/schritte/${encodeURIComponent(schluessel)}/ausweiskorrektur`,
      {
        method: 'POST',
        body: JSON.stringify({ regeln, konzernabschluss: konzernabschluss ?? null }),
      },
    ),

  /** Nimmt einen Schritt aus der Folge -- als geloeschte Fassung (R3). */
  konsolidierungsschrittEntfernen: (schluessel: string, konzernabschluss?: string | null) =>
    hole<Schrittzeile[]>(
      `/api/abschluss/schritte/${encodeURIComponent(schluessel)}`
        + (konzernabschluss ? `?konzernabschluss=${encodeURIComponent(konzernabschluss)}` : ''),
      { method: 'DELETE' },
    ),
  /* --- Das Konzernhandbuch (2.11 Abschnitt 9, R779) ------------------- */

  /**
   * Die Ausgaben des Konzernhandbuchs, juengste zuerst.
   *
   * Jede Zeile sagt selbst, ob sie noch geaendert werden darf: Die Freigabe
   * ist die Grenze, nicht das blosse Rechnen (R98).
   */
  konzernhandbuch: () => hole<Handbuchausgabe[]>('/api/abschluss/konzernhandbuch'),

  /**
   * Legt eine Ausgabe an oder schreibt sie fort — zurueck kommt die volle
   * Liste (R3, R779).
   */
  konzernhandbuchPflegen: (
    ausgabe: number,
    name: string,
    gueltigVon: number,
    gueltigBis: number | null,
    parameter: Regelparameter,
  ) =>
    hole<Handbuchausgabe[]>('/api/abschluss/konzernhandbuch', {
      method: 'POST',
      body: JSON.stringify({ ausgabe, name, gueltigVon, gueltigBis, parameter }),
    }),

  /**
   * Entfernt eine Ausgabe restlos — auf ausdruecklichen Wunsch wird hier
   * wirklich geloescht, solange kein Abschluss damit freigegeben ist (R779).
   */
  konzernhandbuchEntfernen: (ausgabe: number) =>
    hole<Handbuchausgabe[]>(`/api/abschluss/konzernhandbuch/${ausgabe}`, {
      method: 'DELETE',
    }),

  /* --- Die Wechselkurse (7.6) ----------------------------------------- */

  /** Die Wechselkurstabellen — gepflegte und vorgeschlagene (R648). */
  /** Legt eine Wechselkurstabelle an (R683) — zurueck kommt die volle Liste. */
  kurstabelleAnlegen: (von: string, nach: string) =>
    hole<Waehrungspaartafel>('/api/kurse/tabelle', {
      method: 'POST',
      body: JSON.stringify({ von, nach }),
    }),

  waehrungspaare: () => hole<Waehrungspaartafel>('/api/kurse/paare'),

  /** Eine Kurstabelle: Perioden in der Zeile, Kursarten in den Spalten. */
  kurstafel: (von: string, nach: string, vonPeriode?: number, bisPeriode?: number) => {
    const frage = new URLSearchParams({ von, nach })
    if (vonPeriode) {
      frage.set('vonPeriode', String(vonPeriode))
    }

    if (bisPeriode) {
      frage.set('bisPeriode', String(bisPeriode))
    }

    return hole<Kurstafel>(`/api/kurse/tafel?${frage}`)
  },

  /** Setzt einen Kurs von Hand — als neue Fassung, nie als Änderung (R3). */
  kursSetzen: (auftrag: { von: string; nach: string; periode: number; art: Kursart; wert: number }) =>
    hole<Kurstafel>('/api/kurse/zeile', { method: 'POST', body: JSON.stringify(auftrag) }),

  /** Nimmt eine Kurszeile zurück — der Monat steht danach wieder als Lücke da. */
  kursZuruecknehmen: (auftrag: { von: string; nach: string; periode: number; art: Kursart }) =>
    hole<Kurstafel>('/api/kurse/zeile/zuruecknehmen', {
      method: 'POST',
      body: JSON.stringify({ ...auftrag, wert: 1 }),
    }),

  /**
   * Liest eine Kursdatei im festen Schema — und schreibt nichts (R648).
   *
   * Die Datei geht als Formulardaten hinaus, nicht als JSON: Wer sie vorher
   * kodiert, schickt etwas anderes, als er in der Hand hatte.
   */
  kursdatei: (datei: File) => {
    const formular = new FormData()
    formular.append('datei', datei)
    return hole<Kursvorschau>('/api/kurse/datei', { method: 'POST', body: formular })
  },

  /** Holt die EZB-Referenzkurse — und schreibt nichts (R649). */
  ezbkurse: (von: string, nach: string, vonPeriode: number, bisPeriode: number) =>
    hole<Kursvorschau>(
      `/api/kurse/ezb?${new URLSearchParams({
        von,
        nach,
        vonPeriode: String(vonPeriode),
        bisPeriode: String(bisPeriode),
      })}`,
    ),

  /**
   * Holt die EZB-Referenzkurse für **alle** Tabellen auf einmal (R651).
   *
   * **Ohne Paar-Angabe**, und das ist der Punkt: Welche Paare der Lauf umfasst,
   * weiß der Dienst aus dem Bestand an Gesellschaften und Kreisen — die
   * Oberfläche zählt sie nicht auf, sonst müsste sie die Ableitung ein zweites
   * Mal kennen.
   */
  ezbkurseAlle: (vonPeriode: number, bisPeriode: number) =>
    hole<Kursvorschau>(
      `/api/kurse/ezb/alle?${new URLSearchParams({
        vonPeriode: String(vonPeriode),
        bisPeriode: String(bisPeriode),
      })}`,
    ),

  /** Rechnet den Year-to-Date-Kurs — und schreibt nichts (R647). */
  ytdkurse: (von: string, nach: string, vonPeriode: number, bisPeriode: number) =>
    hole<Kursvorschau>(
      `/api/kurse/ytd?${new URLSearchParams({
        von,
        nach,
        vonPeriode: String(vonPeriode),
        bisPeriode: String(bisPeriode),
      })}`,
    ),

  /** Übernimmt genau die Vorschau, die auf dem Schirm stand (R132-Muster). */
  kurseUebernehmen: (vorschau: Kursvorschau) =>
    hole<Kursuebernahme>('/api/kurse/uebernehmen', {
      method: 'POST',
      body: JSON.stringify(vorschau),
    }),

  /** Die historischen Kurse je Gesellschaft und Position (R44). */
  historischeKurse: () => hole<Positionskurs[]>('/api/kurse/historisch'),

  /** Was der dritte Weg zum Eintrittskurs anbietet (R1404). Schreibt nichts. */
  eintrittskursvorschlag: (kreis: string, einheit: string, von: number) =>
    hole<Eintrittskursvorschlag>(
      `/api/kreismitglieder/eintrittskurs?${new URLSearchParams({ kreis, einheit, von: String(von) })}`,
    ),

  /** Die Tabelle der historischen Kurse einer Gesellschaft (2.6 Abschnitt 2.12, R1470). */
  historischeKurseDerGesellschaft: (einheit: string) =>
    hole<HistorischerKurs[]>(`/api/historische-kurse?${new URLSearchParams({ einheit })}`),

  /** Was im Monat nicht zur Lieferung passt — die Aufgabe „Historische Kurse anlegen“ (R1473). */
  historischeKurseOffen: (kreis: string, ledger: string, periode: number) =>
    hole<HistorischeKursluecke[]>(
      `/api/historische-kurse/offen?${new URLSearchParams({ kreis, ledger, periode: String(periode) })}`,
    ),

  /** Der eine Klick: legt jede fehlende Zeile des Monats an (R1473). Zurück kommt, was offen bleibt. */
  historischeKurseAnlegen: (kreis: string, ledger: string, periode: number, eintraege: HistorischerKurseintrag[]) =>
    hole<HistorischeKursluecke[]>('/api/historische-kurse/anlegen', {
      method: 'POST',
      body: JSON.stringify({ kreis, ledger, periode, eintraege }),
    }),

  /** Ändert, löscht oder ergänzt eine Zeile der Tabelle — als neue Fassung. */
  historischerKursSetzen: (zeile: HistorischeKurszeile) =>
    hole<HistorischerKurs[]>('/api/historische-kurse/zeile', {
      method: 'POST',
      body: JSON.stringify(zeile),
    }),

  /* --- Der Bereich Perioden (2.19) ------------------------------------- */

  /**
   * Die Zeitachse des Hauses — `null`, wo keine gesetzt ist (R670).
   *
   * Keine Zeitachse ist kein Störfall: Die Datenbank hat dann keine Zusage,
   * und der Bildschirm sagt genau das.
   */
  zeitachse: () => hole<Zeitachsenstand | null>('/api/perioden/zeitachse'),

  /**
   * Die paarweisen IC-Differenzen eines Konzernabschlusses (2.10 Abschnitt 9).
   *
   * **Beide Sichten kommen in einem Zug** — die Schaltfläche zwischen
   * Verkehrszahl und YTD/Saldo stellt um, was schon da ist, statt
   * nachzuladen.
   */
  icabstimmung: (konzernabschluss: string, werkzeug: string) =>
    hole<Icabstimmblatt | null>(
      '/api/abschluss/ic-abstimmung?konzernabschluss=' +
        encodeURIComponent(konzernabschluss) +
        '&werkzeug=' +
        encodeURIComponent(werkzeug),
    ),

  /** Setzt Anfang und Ende — als Anfügung, nie als Änderung (R3, R670). */
  zeitachseSetzen: (anfang: number, ende: number) =>
    hole<Zeitachsenstand>('/api/perioden/zeitachse', {
      method: 'POST',
      body: JSON.stringify({ anfang, ende }),
    }),

  /** Jede Prüfung der Freigabe mit der Stufe, die für die Periode gilt (2.13 Abschnitt 13, R910). */
  pruefregeln: (periode: number) => hole<Pruefregelzeile[]>(`/api/pruefregeln?periode=${periode}`),

  /** Legt eine Stufe ab einer Periode fest (R910, R911) — und liefert die Liste neu. */
  pruefregelFestlegen: (pruefung: string, stufe: Pruefstufe, gueltigAb: number) =>
    hole<Pruefregelzeile[]>('/api/pruefregeln', {
      method: 'POST',
      body: JSON.stringify({ pruefung, stufe, gueltigAb }),
    }),

  /** Was bei Freigaben übergangen wurde oder ungeprüft blieb (R912, R913). */
  uebergangene: () => hole<{ zeilen: Freigabeprotokollzeile[] }>('/api/freigaben/uebergangene'),
}

/*
  Beträge und Perioden werden dort formatiert, wo die Sprache und das
  Zahlenformat liegen. Hier stehen sie nur noch als Weiterreichung, damit die
  Bauteile ihre Zahlen weiterhin aus einem Stück beziehen.
*/
export { betrag, kurswert, periodenkuerzel, periodenname, tagesdatum } from './sprache'
